/**
 * Chatbot Products Display
 * Shows recommended products after questionnaire completion
 */

function initChatbotProducts() {
  'use strict';

  // DOM elements
  let productsContainer;
  let chatbotBox;

  // Global product image cache - maps shopify_product_id -> { image, url, price }
  let productImageCache = null;
  let productImageCachePromise = null;

  // Cache for re-rendering on layout change
  let lastData = null;
  let lastQuestionnaireData = null;
  // Current routine variants shown in UI (used to sync "Add all" count with cart)
  let currentRoutineVariantIds = [];
  let currentRoutineCartItemIds = [];
  // Expected total "routine product units" when user clicks "Add all"
  // (counts product cards, not just distinct variants)
  let currentRoutineExpectedQty = 0;
  // Slot-level state so AM/PM cards can behave independently even if backend uses same variant.
  let activeRoutineCardSlots = new Set();

  /* Same SVG paths as snippets/icons.liquid (`when 'cart'`) — matches storefront bag icon */
  var THEME_CART_ICON_SVG = '<svg width="17" height="19" viewBox="0 0 17 19" fill="none" class="chatbot-theme-cart-icon" aria-hidden="true"><path d="M13.8624 5.125H3.13686C2.21555 5.125 1.45202 5.83932 1.39074 6.75859L0.749072 16.3836C0.681732 17.3936 1.48288 18.25 2.49519 18.25H14.5041C15.5164 18.25 16.3176 17.3936 16.2502 16.3836L15.6086 6.75859C15.5472 5.83932 14.7837 5.125 13.8624 5.125Z" stroke="currentColor" stroke-width="1.5"/><path d="M12 7.75V4.25C12 2.317 10.433 0.75 8.5 0.75C6.567 0.75 5 2.317 5 4.25V7.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>';

  /** Derive time bucket ('am'|'pm'|'both'|'unknown') from a group of product entries. */
  function getGroupTimeBucket(groupEntries) {
    if (!groupEntries || !groupEntries.length) return 'unknown';
    var buckets = groupEntries.map(function (e) { return getRoutineTimeBucket(e.p); });
    var hasAm   = buckets.some(function (b) { return b === 'am'; });
    var hasPm   = buckets.some(function (b) { return b === 'pm'; });
    var hasBoth = buckets.some(function (b) { return b === 'both'; });
    if (hasBoth || (hasAm && hasPm)) return 'both';
    if (hasAm) return 'am';
    if (hasPm) return 'pm';
    return 'unknown';
  }

  /** Map API category keys to routine step labels, incorporating actual product time. */
  function formatRoutineCategoryTitle(apiKey, timeBucket) {
    if (!apiKey || typeof apiKey !== 'string') return '';
    const raw = apiKey.trim();
    const k = raw.toLowerCase().replace(/[\s-]+/g, '_');

    // Determine time modifier from product data first, then fall back to key name
    var timeLabel = '';
    if (timeBucket === 'am') {
      timeLabel = 'Morning';
    } else if (timeBucket === 'pm') {
      timeLabel = 'Night';
    } else if (!timeBucket || timeBucket === 'unknown') {
      // Fallback: try to read time from the API key itself
      if (k.includes('am') || k.includes('morning') || k.includes('_day') || k.endsWith('_am')) timeLabel = 'Morning';
      else if (k.includes('pm') || k.includes('night') || k.includes('evening') || k.endsWith('_pm')) timeLabel = 'Night';
    }

    const isPreCleanser = (k.includes('pre') && (k.includes('cleans') || k.includes('cleanse'))) || k === 'pre_cleanser' || k === 'precleanser';
    if (isPreCleanser) return 'Pre-cleanser';

    const isFaceWash = (k.includes('face') && (k.includes('wash') || k.includes('cleans'))) || k.includes('face_wash') || k === 'cleanser';
    if (isFaceWash && !isPreCleanser) return timeLabel ? ('Step 1 ' + timeLabel + ' Face Wash') : 'Step 1 Face Wash';

    if (k.includes('serum')) return timeLabel ? ('Step 2 ' + timeLabel + ' Serum') : 'Step 2 Serum';

    if (k.includes('moistur')) return timeLabel ? ('Step 3 ' + timeLabel + ' Moisturiser') : 'Step 3 Moisturiser';

    if (k.includes('sun')) return 'Step 4 Sunscreen';

    return raw.split('_').map(function (w) {
      if (!w) return '';
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  /** Routine sort: AM (0) first, then BOTH/AM+PM (1), then PM (2), unknown last */
  function getApplicationTimeSortRank(p) {
    const raw = String((p && (p.application_time || p.usage_time || p.recommended_time || p.time_of_use)) || '').toUpperCase();
    if (!raw.trim()) return 99;
    const both = raw.includes('BOTH') || raw.includes('AM/PM');
    const am = raw.includes('AM') || raw.includes('MORNING') || raw.includes('DAY');
    const pm = raw.includes('PM') || raw.includes('NIGHT') || raw.includes('EVENING');
    if (both) return 1;
    if (am && !pm) return 0;
    if (pm && !am) return 2;
    if (am && pm) return 1;
    return 99;
  }

  function sortRoutineEntriesByScore(entries) {
    if (!entries || !entries.length) return [];
    return entries.slice().sort(function (a, b) {
      var sa = parseInt(String((a && a.p && a.p.total_score) != null ? a.p.total_score : 0), 10);
      var sb = parseInt(String((b && b.p && b.p.total_score) != null ? b.p.total_score : 0), 10);
      if (isNaN(sa)) sa = 0;
      if (isNaN(sb)) sb = 0;
      if (sb !== sa) return sb - sa;
      return (a.i || 0) - (b.i || 0);
    });
  }

  /**
   * Serum: show up to 2 distinct morning (AM) and up to 2 distinct night (PM) products,
   * each in its own parent card (not mixed into a single "one AM + one PM" cap).
   */
  function buildSerumParentGroups(uniqueProducts) {
    if (!uniqueProducts || !uniqueProducts.length) return [];
    var am = [];
    var pm = [];
    var both = [];
    var unk = [];
    uniqueProducts.forEach(function (e) {
      var b = getRoutineTimeBucket(e.p);
      if (b === 'am') am.push(e);
      else if (b === 'pm') pm.push(e);
      else if (b === 'both') both.push(e);
      else unk.push(e);
    });
    am = sortRoutineEntriesByScore(am).slice(0, 2);
    pm = sortRoutineEntriesByScore(pm).slice(0, 2);
    var groups = [];
    if (am.length) groups.push({ timeBucket: 'am', entries: am });
    if (pm.length) groups.push({ timeBucket: 'pm', entries: pm });
    if (!groups.length) {
      if (both.length) {
        groups.push({ timeBucket: 'both', entries: sortRoutineEntriesByScore(both).slice(0, 2) });
      } else if (unk.length) {
        var lim = sortAndLimitRoutineProducts(unk);
        if (lim.length) groups.push({ timeBucket: getGroupTimeBucket(lim), entries: lim });
      }
    }
    return groups;
  }

  function categoryKeyIsSerum(key) {
    return String(key || '').toLowerCase().includes('serum');
  }

  /** At most two products per category; prefer one AM + one PM when both exist */
  function sortAndLimitRoutineProducts(items) {
    if (!items || !items.length) return items;
    const rank = function (entry) { return getApplicationTimeSortRank(entry.p); };
    const normName = function (entry) {
      return String((entry && entry.p && (entry.p.product_name || entry.p.name || entry.p.title)) || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    };
    const sorted = items.slice().sort(function (a, b) { return rank(a) - rank(b); });
    const am = sorted.filter(function (x) { return rank(x) === 0; });
    const both = sorted.filter(function (x) { return rank(x) === 1; });
    const pm = sorted.filter(function (x) { return rank(x) === 2; });

    // Rule: if we have 2+ AM products with different names, keep those 2 AM products.
    if (am.length >= 2) {
      var amDifferentNames = [];
      var seenAmNames = new Set();
      for (var i = 0; i < am.length; i++) {
        var n = normName(am[i]);
        if (n && !seenAmNames.has(n)) {
          amDifferentNames.push(am[i]);
          seenAmNames.add(n);
        }
        if (amDifferentNames.length >= 2) break;
      }
      if (amDifferentNames.length >= 2) return amDifferentNames.slice(0, 2);
    }

    const out = [];
    if (am.length) out.push(am[0]);
    if (out.length < 2 && pm.length) out.push(pm[0]);
    if (out.length < 2 && both.length) out.push(both[0]);
    if (out.length < 2) {
      sorted.forEach(function (x) {
        if (out.length < 2 && out.indexOf(x) === -1) out.push(x);
      });
    }
    return out.slice(0, 2);
  }

  function getRoutineTimeBucket(p) {
    var raw = String((p && (p.application_time || p.usage_time || p.recommended_time || p.time_of_use)) || '').toUpperCase();
    var both = raw.includes('BOTH') || raw.includes('AM/PM');
    var am = raw.includes('AM') || raw.includes('MORNING') || raw.includes('DAY');
    var pm = raw.includes('PM') || raw.includes('NIGHT') || raw.includes('EVENING');
    if (both || (am && pm)) return 'both';
    if (am) return 'am';
    if (pm) return 'pm';
    return 'unknown';
  }

  function getComparableProductName(p) {
    return String(getProductName(p) || '')
      .toLowerCase()
      // Ignore routine-time tags in names so "Serum AM" and "Serum PM" match as same product.
      .replace(/\b(am|pm|day|night|morning|evening)\b/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Same name + one AM + one PM => render as separate individual cards */
  function shouldSplitIntoIndividualCards(entries) {
    if (!entries || entries.length !== 2) return false;
    var n1 = getComparableProductName(entries[0].p);
    var n2 = getComparableProductName(entries[1].p);
    if (!n1 || !n2 || n1 !== n2) return false;
    var t1 = getRoutineTimeBucket(entries[0].p);
    var t2 = getRoutineTimeBucket(entries[1].p);
    var amLike1 = t1 === 'am' || t1 === 'both';
    var pmLike1 = t1 === 'pm' || t1 === 'both';
    var amLike2 = t2 === 'am' || t2 === 'both';
    var pmLike2 = t2 === 'pm' || t2 === 'both';
    // Split only when pair represents AM + PM coverage for same base product name.
    return (amLike1 && pmLike2) || (pmLike1 && amLike2);
  }

  function shouldShowEitherOneSeparator(entries) {
    if (!entries || entries.length !== 2) return false;
    var n1 = getComparableProductName(entries[0].p);
    var n2 = getComparableProductName(entries[1].p);
    if (!n1 || !n2 || n1 === n2) return false;
    var t1 = getRoutineTimeBucket(entries[0].p);
    var t2 = getRoutineTimeBucket(entries[1].p);
    return t1 === t2 && (t1 === 'am' || t1 === 'pm' || t1 === 'both');
  }

  function clearInjectedTopHeaderActions() {
    const injected = document.getElementById('chatbot-products-top-actions');
    if (injected && injected.parentNode) injected.parentNode.removeChild(injected);

    const menuDropdown = document.getElementById('chatbot-products-menu-dropdown');
    if (menuDropdown && menuDropdown.parentNode) menuDropdown.parentNode.removeChild(menuDropdown);

    const skinDropdown = document.getElementById('chatbot-products-skin-dropdown');
    if (skinDropdown && skinDropdown.parentNode) skinDropdown.parentNode.removeChild(skinDropdown);
  }

  /**
   * Set DOM references
   */
  let onRetake;
  let onRetakeQuestionnaire;
  let onFaceScan;

  /**
   * Set DOM references
   */
  /** Keep intro video when re-rendering the product rail */
  function clearProductsRailExceptMedia() {
    if (!productsContainer) return;
    Array.from(productsContainer.children).forEach(function (el) {
      if (el && el.classList && el.classList.contains('chatbot-products-media-stack')) return;
      el.remove();
    });
  }

  /** Cards first, then intro video — always last child in #chatbot-products */
  function ensureProductsMediaStackLast() {
    if (!productsContainer) return;
    var stack = productsContainer.querySelector('.chatbot-products-media-stack');
    if (stack && stack.parentNode === productsContainer) {
      productsContainer.appendChild(stack);
    }
  }

  function setDOMReferences(refs) {
    productsContainer = refs.productsContainer;
    chatbotBox = refs.chatbotBox;
    onRetake = refs.onRetake;
    onRetakeQuestionnaire = refs.onRetakeQuestionnaire;
    onFaceScan = refs.onFaceScan;
    if (refs.onProductClick) {
      // Allow overriding the default click handler
      handleProductClickHandler = refs.onProductClick;
    }
  }

  /**
   * Display products list with images
   */
  function displayProducts(products = []) {
    if (!productsContainer) return;

    productsContainer.innerHTML = '';

    // Only show products if provided from API - no hardcoded fallback
    if (!products || products.length === 0) {
      productsContainer.style.display = 'none';
      return;
    }

    // Add Results Video if available
    if (window.RESULTS_VIDEO_URL) {
      const videoCont = document.createElement('div');
      videoCont.style.padding = '15px';
      const vid = document.createElement('video');
      vid.className = 'welcome-video';
      vid.setAttribute('playsinline', '');
      vid.style.cssText = 'width:100%; height:auto; max-height:min(34vh, 280px); object-fit:contain; border-radius:12px; margin-bottom:10px; display:block;';
      vid.innerHTML = '<source src="' + window.RESULTS_VIDEO_URL + '">';
      vid.onended = function () { this.currentTime = 0; this.pause(); };
      videoCont.appendChild(vid);
      productsContainer.appendChild(videoCont);
    }

    const productsGrid = document.createElement('div');
    productsGrid.className = 'products-grid';

    products.forEach(function (product) {
      const productCard = createProductCard(product);
      productsGrid.appendChild(productCard);
    });

    productsContainer.appendChild(productsGrid);

    // Helper text so user understands the flow (below products, in the space at bottom)
    var helperEl = document.createElement('div');
    helperEl.className = 'recommended-products-helper';
    helperEl.innerHTML = [
      'Our recommended products are above.',
      'Tap a product to see details and FAQs.',
      'Click Know More to explore ingredients and deeper insights.'
    ].map(function (line) { return '<p>' + line + '</p>'; }).join('');
    productsContainer.appendChild(helperEl);

    productsContainer.style.display = 'block';
  }

  /**
   * Resolve product image from process-assessment / Shopify / generic APIs.
   */
  function getProductImage(product) {
    if (!product) return null;

    // Support recommendations API schema (often doesn't provide image directly)
    if (product.image_url) return product.image_url;

    if (product.image) {
      if (typeof product.image === 'string') return product.image;
      if (product.image.src) return product.image.src;
      if (product.image.url) return product.image.url;
    }

    // Fallback search keywords if image is missing - the UI will use a stylized placeholder
    // Or we will try to resolve it from the window.Shopify object if available

    if (product.featured_image || product.img_url || product.thumbnail)
      return product.featured_image || product.img_url || product.thumbnail;

    if (Array.isArray(product.images) && product.images[0]) {
      var i0 = product.images[0];
      return typeof i0 === 'string' ? i0 : (i0.src || i0.url || null);
    }

    return null;
  }

  function getProductName(p) {
    if (!p) return 'Product';
    return p.product_name || p.product || p.title || p.name || p.product_title || 'Product';
  }

  function getProductDescription(p) {
    if (!p) return '';

    // Priority 1: Specifically matched concerns count
    if (p.matched_concerns && Array.isArray(p.matched_concerns) && p.matched_concerns.length > 0) {
      return `Perfectly matched for ${p.matched_concerns.length} of your concerns.`;
    }

    // Priority 2: Traditional concerns_satisfied field
    if (p.concerns_satisfied) {
      return `Addresses ${p.concerns_satisfied} of your skin concerns.`;
    }

    var raw = p.description || p.body || p.body_html || p.description_html || '';
    if (typeof raw !== 'string') return '';
    var t = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return t.length > 120 ? t.substring(0, 117) + '...' : t;
  }

  function getProductPrice(p) {
    if (!p) return null;
    if (p.price != null && p.price !== '') { var n = parseFloat(p.price); return isNaN(n) ? null : n; }
    if (p.variants && p.variants[0] && p.variants[0].price != null) {
      var vn = parseFloat(p.variants[0].price); return isNaN(vn) ? null : vn;
    }
    return null;
  }

  function getProductUrl(p) {
    if (!p) return null;

    // If we have shopify_product_id, we can potentially link to search if handle is missing
    if (p.url || p.permalink || p.onlineStoreUrl || p.product_url) return p.url || p.permalink || p.onlineStoreUrl || p.product_url;

    // If we have product_name, we can link to a search result as a robust fallback
    if (p.product_name) {
      return '/search?q=' + encodeURIComponent(p.product_name);
    }

    return null;
  }

  /**
   * Helper to extract numeric ID from Shopify GID or mixed formats
   */
  function extractNumericId(id) {
    if (!id) return null;
    if (typeof id === 'number') return id;
    if (typeof id !== 'string') return id;

    // Handle GID format: gid://shopify/ProductVariant/123456789
    if (id.includes('gid://')) {
      return id.split('/').pop();
    }

    // Return numeric part if it's mixed or just string
    return id;
  }

  /**
   * Helper to resolve variant ID from a product object
   */
  function resolveVariantId(product) {
    if (!product) return null;
    let vid =
      product.variantId ||
      product.variant_id ||
      product.shopify_variant_id ||
      (product.variants && product.variants[0] ? product.variants[0].id : null);

    // Check if we have product_id that might actually be the variant_id (common in some recommend APIs)
    if (!vid && product.product_id && !isNaN(product.product_id)) {
      vid = product.product_id;
    }

    if (!vid && product.shopify_product_id && productImageCache && productImageCache[String(product.shopify_product_id)]) {
      const cached = productImageCache[String(product.shopify_product_id)];
      const bucket = getRoutineTimeBucket(product);
      if (cached && cached.variantIdByTime && bucket && bucket !== 'unknown') {
        if (bucket === 'am' && cached.variantIdByTime.am) vid = cached.variantIdByTime.am;
        else if (bucket === 'pm' && cached.variantIdByTime.pm) vid = cached.variantIdByTime.pm;
        else if (bucket === 'both' && cached.variantIdByTime.default) vid = cached.variantIdByTime.default;
      }
      if (!vid) vid = cached && cached.variantId ? cached.variantId : null;
    }
    if (!vid && product.handle && productImageCache) {
      const cachedKey = Object.keys(productImageCache).find(key => productImageCache[key].handle === product.handle);
      if (cachedKey) {
        const cached = productImageCache[cachedKey];
        const bucket = getRoutineTimeBucket(product);
        if (cached && cached.variantIdByTime && bucket && bucket !== 'unknown') {
          if (bucket === 'am' && cached.variantIdByTime.am) vid = cached.variantIdByTime.am;
          else if (bucket === 'pm' && cached.variantIdByTime.pm) vid = cached.variantIdByTime.pm;
          else if (bucket === 'both' && cached.variantIdByTime.default) vid = cached.variantIdByTime.default;
        }
        if (!vid) vid = cached && cached.variantId ? cached.variantId : null;
      }
    }

    return extractNumericId(vid);
  }

  function chatbotVisuallyHidden(text) {
    return '<span class="chatbot-visually-hidden">' + text + '</span>';
  }

  function countProductsWithVariants(products) {
    if (!products || !products.length) return 0;
    var n = 0;
    for (var i = 0; i < products.length; i++) {
      if (normalizeCartVariantId(resolveVariantId(products[i]))) n++;
    }
    return n;
  }

  function setCurrentRoutineVariantIdsFromProducts(products) {
    if (!products || !products.length) {
      currentRoutineVariantIds = [];
      currentRoutineCartItemIds = [];
      currentRoutineExpectedQty = 0;
      return;
    }
    var set = new Set();
    var itemIdSet = new Set();
    for (var i = 0; i < products.length; i++) {
      const p = products[i];
      // Routine identity must be variant-based to avoid double counting cards that share product_id.
      var vid = normalizeCartVariantId(resolveVariantId(p));
      var cartItemId = getCartItemId(p);

      if (vid) set.add(String(vid));
      if (cartItemId) itemIdSet.add(String(cartItemId));
    }
    currentRoutineVariantIds = Array.from(set);
    currentRoutineCartItemIds = Array.from(itemIdSet);
    currentRoutineExpectedQty = products.length || 0;
  }

  function getRoutineProductsInCartCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    // Primary count path: cart_item_id (shopify_product_id + application_time)
    // This keeps AM/PM entries independent even when they share a variant.
    const routineItemIdsSet = new Set();
    document.querySelectorAll('.product-card').forEach(function (card) {
      const cid = card.getAttribute('data-cart-item-id');
      if (cid) routineItemIdsSet.add(String(cid));
    });
    if (currentRoutineCartItemIds && currentRoutineCartItemIds.length) {
      currentRoutineCartItemIds.forEach(function (id) {
        if (id) routineItemIdsSet.add(String(id));
      });
    }

    if (routineItemIdsSet.size > 0) {
      let inCount = 0;
      cart.items.forEach(function (item) {
        const lineItemId = getCartItemIdFromCartLine(item);
        if (lineItemId && routineItemIdsSet.has(String(lineItemId))) {
          inCount += 1; // line-count, not qty-count
        }
      });
      return inCount;
    }

    // Fallback for legacy cart lines without properties.cart_item_id
    const routineIdsSet = new Set();
    document.querySelectorAll('.product-card').forEach(function(card) {
      const vid = card.getAttribute('data-variant-id');
      if (vid) routineIdsSet.add(String(vid));
    });
    if (currentRoutineVariantIds && currentRoutineVariantIds.length) {
      currentRoutineVariantIds.forEach(function (id) { routineIdsSet.add(String(id)); });
    }
    if (routineIdsSet.size === 0) return 0;
    let fallbackCount = 0;
    cart.items.forEach(function(item) {
      const vid = normalizeCartVariantId(item.variant_id || item.id);
      if (vid && routineIdsSet.has(String(vid))) fallbackCount += 1;
    });
    return fallbackCount;
  }

  function refreshAddAllButtonsFromCart(cart) {
    var total = currentRoutineExpectedQty;
    var inCount = getRoutineProductsInCartCount(cart);
    var isAllAdded = total > 0 && inCount >= total;

    try {
      localStorage.setItem('chatbot_last_routine_total', String(total));
      localStorage.setItem('chatbot_last_routine_in_count', String(inCount));
    } catch (e) { /* ignore */ }

    document.querySelectorAll('.add-all-bag-btn').forEach(function (btn) {
      if (!btn) return;
      // Keep loading label untouched while request is in progress.
      if (btn.disabled) return;
      const isLocked = btn.getAttribute('data-sync-lock') === 'true';
      // PERSISTENCE LOCK: after Add All, cart.js can lag briefly — keep optimistic UI only while locked.
      // Clear stale data-assumed-added when cart has no routine lines (fixes false counts after retake).
      if (inCount > 0) {
        btn.setAttribute('data-assumed-added', 'true');
      } else if (!isLocked) {
        btn.removeAttribute('data-assumed-added');
      }
      const isAssumedAdded = btn.getAttribute('data-assumed-added') === 'true';
      var hasAnyRoutineInBag = inCount > 0 || (isAssumedAdded && isLocked);

      if (isLocked && inCount < total) {
        console.log("[ChatbotSync] Sync lock active. Preserving UI count.");
        return;
      }

      btn.setAttribute('data-bag-count-visible', hasAnyRoutineInBag ? 'true' : 'false');
      btn.setAttribute('data-all-added', isAllAdded ? 'true' : 'false');
      btn.setAttribute('title', hasAnyRoutineInBag ? 'View bag' : 'Add all routine products to bag');
      
      btn.innerHTML = renderAddAllBagButtonInner(inCount, hasAnyRoutineInBag);
    });

    // ALSO: Update individual product cards to show their quantity in cart
    if (cart && Array.isArray(cart.items)) {
      // Find one button to check for sync lock
      const referenceBtn = document.querySelector('.add-all-bag-btn');
      const isLocked = referenceBtn && referenceBtn.getAttribute('data-sync-lock') === 'true';

      document.querySelectorAll('.product-card').forEach(function (card) {
        if (typeof card.updateUIState !== 'function') return;
        var cardVid = card.getAttribute('data-variant-id');
        var cardPid = card.getAttribute('data-product-id');
        var cardCartItemId = card.getAttribute('data-cart-item-id') || '';
        var normCardPid = cardPid ? String(normalizeCartVariantId(cardPid)) : '';
        var cardTime = card.getAttribute('data-time-bucket') || '';

        // AM/PM/both slots often share one Shopify variant id; matching by variant would show the same
        // cart line on every sibling card. Only cart_item_id (line properties) is safe here.
        if (cardTime === 'am' || cardTime === 'pm' || cardTime === 'both') {
          if (!cardCartItemId) return;
        }

        // If the card was created before productImageCache was ready, it may not have data-variant-id.
        // Recover it from the cache using the product id so we can sync UI correctly.
        if (!cardVid && cardPid && productImageCache && productImageCache[String(cardPid)]) {
          var cached = productImageCache[String(cardPid)];
          var recoveredRaw = cached && cached.variantId;
          if (cached && cached.variantIdByTime && cardTime) {
            if (cardTime === 'am' && cached.variantIdByTime.am) recoveredRaw = cached.variantIdByTime.am;
            else if (cardTime === 'pm' && cached.variantIdByTime.pm) recoveredRaw = cached.variantIdByTime.pm;
            else if (cardTime === 'both' && cached.variantIdByTime.default) recoveredRaw = cached.variantIdByTime.default;
          }
          var recovered = normalizeCartVariantId(recoveredRaw);
          if (recovered) {
            cardVid = String(recovered);
            card.setAttribute('data-variant-id', cardVid);
          }
        }

        // For AM/PM timed slots, NEVER fallback to product_id matching.
        // Product fallback links timed sibling cards (AM/PM) together and causes mirrored qty updates.
        if (!cardVid && (cardTime === 'am' || cardTime === 'pm' || cardTime === 'both')) return;

        // If we still can't match by variant (non-timed cards), we can fallback to product id.
        if (!cardVid && !normCardPid) return;

        const cartItem = cart.items.find(function (it) {
          if (cardCartItemId) {
            return getCartItemIdFromCartLine(it) === cardCartItemId;
          }
          if (cardTime === 'am' || cardTime === 'pm' || cardTime === 'both') {
            return false;
          }
          if (cardVid) {
            const itVid = normalizeCartVariantId(it && (it.variant_id || it.id));
            return itVid != null && String(itVid) === cardVid;
          }

          if (normCardPid) {
            const itPid = normalizeCartVariantId(it && (it.product_id || null));
            return itPid != null && String(itPid) === normCardPid;
          }

          return false;
        });

        let qty = cartItem ? cartItem.quantity : 0;
        
        // Respect lock: if we're locked and server says 0, but we assume 1+, don't revert
        if (isLocked && qty === 0) {
            const currentQtyEl = card.querySelector('.qty-display');
            const isShown = card.querySelector('.product-qty-wrapper').style.display !== 'none';
            if (isShown) return; // Keep existing UI
        }

        card.updateUIState(qty);
      });
    }
  }

  /**
   * Optimistically update the header count before API returns
   */
  function syncHeaderCountOptimistically() {
    let assumedInCount = 0;
    let hasAny = false;
    document.querySelectorAll('.product-card').forEach(function (card) {
      // Find the quantity display in this card
      const qtyEl = card.querySelector('.qty-display');
      const isVisible = card.querySelector('.product-qty-wrapper').style.display !== 'none';
      if (isVisible && qtyEl) {
        const q = parseInt(qtyEl.textContent, 10) || 0;
        if (q > 0) {
          assumedInCount += q;
          hasAny = true;
        }
      }
    });

    // Also update any other routine products that might be in cart but not current cards 
    // (though usually we just care about what's visible for the header sync here)
    document.querySelectorAll('.add-all-bag-btn').forEach(function (btn) {
      if (btn.disabled && !btn.getAttribute('data-sync-lock')) return; 
      btn.innerHTML = renderAddAllBagButtonInner(assumedInCount, hasAny);
    });
  }

  function syncStorefrontCartCount(cart) {
    // Allow callers to pass the cart returned by cart/add.js or cart/update.js
    // to avoid eventual-consistency issues with a second cart.js fetch.
    function applyCartCount(cartToApply) {
      if (!cartToApply) return;
      var itemCount = (typeof cartToApply.item_count === 'number') ? cartToApply.item_count : null;
      if (itemCount == null) {
        // Fallback: compute from items if item_count is absent.
        if (Array.isArray(cartToApply.items)) {
          itemCount = cartToApply.items.reduce(function (sum, it) {
            var q = parseInt(String(it && it.quantity), 10);
            return sum + (isNaN(q) ? 0 : Math.max(0, q));
          }, 0);
        }
      }
      if (itemCount == null || isNaN(itemCount)) return;

      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        if (!el) return;

        if (itemCount === 0) {
          el.classList.add('hidden');
          el.classList.remove('large-items');
          el.textContent = '';
          return;
        }

        if (itemCount > 99) {
          el.classList.remove('hidden');
          el.classList.add('large-items');
          el.textContent = '';
          return;
        }

        el.classList.remove('hidden', 'large-items');
        el.textContent = String(itemCount);
      });

      try {
        localStorage.setItem('chatbot_last_cart_item_count', String(itemCount));
      } catch (e) { /* ignore */ }
    }

    if (cart) {
      applyCartCount(cart);
      refreshAddAllButtonsFromCart(cart);
      return;
    }

    // Apply cached count immediately (prevents visible "missing count" flash).
    try {
      var cached = parseInt(localStorage.getItem('chatbot_last_cart_item_count') || '', 10);
      if (!isNaN(cached)) applyCartCount({ item_count: cached, items: [] });
    } catch (e) { /* ignore */ }

    // Apply cached routine count immediately too (only if routine total matches).
    try {
      var cachedTotal = parseInt(localStorage.getItem('chatbot_last_routine_total') || '', 10);
      var cachedIn = parseInt(localStorage.getItem('chatbot_last_routine_in_count') || '', 10);
      if (!isNaN(cachedTotal) && !isNaN(cachedIn) && cachedTotal === currentRoutineExpectedQty && currentRoutineExpectedQty > 0) {
        var hasAnyRoutineInBagCached = cachedIn > 0;
        var isAllAddedCached = cachedIn >= cachedTotal;
        document.querySelectorAll('.add-all-bag-btn').forEach(function (btn) {
          if (!btn || btn.disabled) return;
          // If a "sync lock" is active, don't override it.
          var isLocked = btn.getAttribute('data-sync-lock') === 'true';
          if (isLocked) return;
          btn.setAttribute('data-bag-count-visible', hasAnyRoutineInBagCached ? 'true' : 'false');
          btn.setAttribute('data-all-added', isAllAddedCached ? 'true' : 'false');
          btn.setAttribute('title', hasAnyRoutineInBagCached ? 'View bag' : 'Add all routine products to bag');
          btn.innerHTML = renderAddAllBagButtonInner(cachedIn, hasAnyRoutineInBagCached);
        });
      }
    } catch (e) { /* ignore */ }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    fetch(root + 'cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        applyCartCount(cart);
        refreshAddAllButtonsFromCart(cart);
      })
      .catch(function () { /* ignore */ });
  }

  /** Idle: icon + “Add all to bag” only (no “0”). If count > 0: icon + count only. */
  function renderAddAllBagButtonInner(count, isAdded) {
    var n = Math.max(0, count);
    var display = n > 99 ? '99+' : String(n);
    
    // STRICT RULE: If ANY routine items are in bag (count > 0), show count UI, NEVER text label.
    if (n > 0) {
      return (
        '<span class="add-all-bag-btn-inner add-all-bag-btn-inner--added">' +
        THEME_CART_ICON_SVG +
        '<span class="add-all-bag-count add-all-bag-count--solo">' + display + '</span>' +
        chatbotVisuallyHidden('Added ' + display + ' products. Click to remove all from bag.') +
        '</span>'
      );
    }
    
    // Only show idle text if literally zero items match
    return (
      '<span class="add-all-bag-btn-inner add-all-bag-btn-inner--idle">' +
      THEME_CART_ICON_SVG +
      '<span class="add-all-bag-label">Add all to bag</span>' +
      chatbotVisuallyHidden('Add all to bag') +
      '</span>'
    );
  }

  function getProductAddToBagMarkup() {
    return '<span class="product-add-bag-text-only">Add to bag</span>';
  }

  function normalizeCartVariantId(raw) {
    if (raw == null || raw === '') return null;
    var s = extractNumericId(raw);
    if (s == null || s === '') return null;
    var n = parseInt(String(s), 10);
    return isNaN(n) ? null : n;
  }

  function getApplicationTimeToken(product) {
    var b = getRoutineTimeBucket(product);
    if (b === 'am' || b === 'pm' || b === 'both') return b;
    var raw = String((product && (product.application_time || product.usage_time || product.recommended_time || product.time_of_use)) || '').trim().toLowerCase();
    if (!raw) return 'unknown';
    return raw.replace(/[\[\]'"\s]+/g, '').replace(/,+/g, '_');
  }

  function getCartItemId(product) {
    var pid = String(product && (product.shopify_product_id || product.product_id || product.id || product.handle || '')).trim();
    if (!pid) return '';
    var base = pid + '_' + getApplicationTimeToken(product);
    // Scope cart identity to recommendation session so retake/new results don't auto-bind
    // to previous session line items.
    var scope = String((product && (product._cartIdentityScope || product.assessment_session_id || product.recommendation_id)) || '').trim();
    return scope ? (base + '_' + scope) : base;
  }

  function getCartItemIdFromCartLine(it) {
    if (!it || !it.properties) return '';
    var v = it.properties.cart_item_id || it.properties._cart_item_id || '';
    return String(v || '').trim();
  }

  /** Bulk /cart/add.js, then one-by-one fallback (fixes Failed when bulk rejects). */
  async function postCartAddLineItems(lineItems) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var url = root + 'cart/add.js';
    var clean = lineItems.filter(function (x) { return x && x.id; }).map(function (x) {
      var out = { id: x.id, quantity: Math.max(1, parseInt(x.quantity, 10) || 1) };
      if (x.cart_item_id) out.properties = { cart_item_id: String(x.cart_item_id) };
      return out;
    });
    if (!clean.length) return { ok: false, message: 'No variant ids' };

    function parseErr(data) {
      if (!data || typeof data !== 'object') return 'Add to cart failed';
      if (data.description) return String(data.description);
      if (data.message) return String(data.message);
      if (data.errors) return typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
      return 'Add to cart failed';
    }

    var bulkRes = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: clean })
    });
    var bulkData = await bulkRes.json().catch(function () { return {}; });
    if (bulkRes.ok && bulkData && !bulkData.errors) {
      return { ok: true, data: bulkData };
    }

    for (var i = 0; i < clean.length; i++) {
      var one = clean[i];
      var r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: one.id, quantity: one.quantity, properties: one.properties || undefined }] })
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok || (d && d.errors)) {
        return { ok: false, message: parseErr(d) };
      }
    }
    return { ok: true, data: {} };
  }

  /**
   * Add product to cart
   */
  function addToCart(variantId, btn, quantity = 1, product = null) {
    // Optimistic Update
    syncHeaderCountOptimistically();
    
    var nid = normalizeCartVariantId(variantId);
    if (!nid) return Promise.reject('No variant ID');

    var isIconBag = btn.classList && btn.classList.contains('product-action-btn');
    var originalHtml = isIconBag ? btn.innerHTML : null;

    if (isIconBag) {
      btn.innerHTML = '<span class="product-add-bag-text-only">Adding…</span>';
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.textContent = 'Adding...';
    }
    btn.disabled = true;
    btn.style.opacity = '0.7';

    var cartItemId = product ? getCartItemId(product) : '';
    return fetch(window.Shopify ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: nid,
          quantity: quantity,
          properties: cartItemId ? { cart_item_id: cartItemId } : undefined
        }]
      })
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        if (data.status === 422 || data.error) {
          throw new Error(data.description || 'Error adding to cart');
        }

        if (isIconBag) {
          btn.innerHTML = originalHtml;
          btn.removeAttribute('aria-busy');
        } else {
          btn.textContent = 'Added';
        }

        // Persist "Added" state to the header button
        document.querySelectorAll('.add-all-bag-btn').forEach(b => b.setAttribute('data-assumed-added', 'true'));

        // Dispatch event so themes can update cart drawer/count
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
        // Also try standard Shopify events
        document.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        syncStorefrontCartCount(data);

        setTimeout(() => {
          // We do not reset here anymore for the new flow, UI handles visual transition
          // But keeping partial reset just in case
          btn.disabled = false;
          btn.style.opacity = '1';
        }, 500);

        return data;
      })
      .catch((error) => {
        if (isIconBag) {
          btn.innerHTML = originalHtml;
          btn.removeAttribute('aria-busy');
        } else {
          btn.textContent = 'Retry';
        }
        btn.disabled = false;
        btn.style.opacity = '1';
        throw error;
      });
  }

  function updateCartItem(variantId, quantity, callback, product = null) {
    // Optimistic Update
    syncHeaderCountOptimistically();
    var root = window.Shopify ? window.Shopify.routes.root : '/';
    var cartItemId = product ? getCartItemId(product) : '';
    var normalizedVid = normalizeCartVariantId(variantId);

    // SAFE fallback: find the specific line by variant ID and use cart/change.js.
    // NEVER use cart/update.js with a variantId for removal — it removes ALL lines
    // sharing that variant, not just the one the user clicked.
    var doSafeLineChange = function (cartData) {
      if (cartData && Array.isArray(cartData.items)) {
        // Prefer matching by cart_item_id property (most precise)
        var target = null;
        if (cartItemId) {
          target = cartData.items.find(function (it) {
            return getCartItemIdFromCartLine(it) === cartItemId;
          });
        }
        // Fall back to matching by variant ID (first matching line only)
        if (!target && normalizedVid) {
          target = cartData.items.find(function (it) {
            return normalizeCartVariantId(it.variant_id || it.id) === normalizedVid;
          });
        }
        if (target && (target.key || target.id)) {
          return fetch(root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: target.key || String(target.id), quantity: quantity })
          });
        }
      }
      // Only use the bulk variant update as a last resort for qty > 0 (adding).
      // For removal (qty === 0), log a warning and do nothing to prevent cascade removals.
      if (quantity === 0) {
        console.warn('[ChatbotCart] Could not find specific cart line to remove for variantId:', variantId, '— skipping to avoid removing wrong items.');
        return Promise.resolve({ ok: true, json: () => Promise.resolve(cartData || {}) });
      }
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: quantity } })
      });
    };

    var reqPromise = fetch(root + 'cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return doSafeLineChange(cart); })
      .catch(function () {
        // If cart fetch fails and we're removing, bail safely
        if (quantity === 0) return Promise.resolve({ ok: true, json: () => Promise.resolve({}) });
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [variantId]: quantity } })
        });
      });

    reqPromise
      .then(r => typeof r.json === 'function' ? r.json() : r)
      .then(data => {
        var routineInCount = getRoutineProductsInCartCount(data);
        document.querySelectorAll('.add-all-bag-btn').forEach(b => {
          if (quantity > 0) b.setAttribute('data-assumed-added', 'true');
          else if (routineInCount === 0) b.removeAttribute('data-assumed-added');
        });

        // Dispatch refresh
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        syncStorefrontCartCount(data);
        if (callback) callback(true, data);
      })
      .catch(e => {
        if (callback) callback(false);
      });
  }

  /**
   * Create a product card: image, name, description, price.
   */
  function createProductCard(product, categoryLabel = null) {
    var card = document.createElement('div');
    card.className = 'product-card';
    card.setAttribute('data-product-id', product.shopify_product_id || product.id || product.product_id || product.handle || '');
    card.setAttribute('data-time-bucket', getRoutineTimeBucket(product));
    card.setAttribute('data-cart-item-id', getCartItemId(product));
    const slotKey = [
      String(product.shopify_product_id || product.id || product.product_id || product.handle || ''),
      String(getRoutineTimeBucket(product) || ''),
      String(getComparableProductName(product) || '')
    ].join('|');
    card.setAttribute('data-slot-key', slotKey);

    // Card clickable for guided chat — image area is primary click target; exclude buttons
    card.style.cursor = 'pointer';
    card.style.touchAction = 'manipulation';
    card.onclick = function (e) {
      if (e.target.closest('.know-more-btn') || e.target.closest('.product-action-btn') ||
          e.target.closest('.view-cart-btn') || e.target.closest('.qty-btn')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handleProductClickHandler(product, card);
    };

    // Create Category Header (Above Image)
    if (categoryLabel) {
      var catHeader = document.createElement('div');
      catHeader.className = 'product-card-category-header';
      catHeader.textContent = categoryLabel;
      card.appendChild(catHeader);
    }

    var imageContainer = document.createElement('div');
    imageContainer.className = 'product-image-container';
    imageContainer.style.cursor = 'pointer';

    var img = document.createElement('img');
    var imageUrl = getProductImage(product);

    // High-quality premium placeholder 
    var place = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23fcfcfc" width="200" height="200"/%3E%3Cpath d="M100 50c-27.6 0-50 22.4-50 50s22.4 50 50 50 50-22.4 50-50-22.4-50-50-50zm0 90c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40z" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="115" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="10" font-weight="600" fill="%23cccccc"%3ECODESKIN CARE%3C/text%3E%3C/svg%3E';

    img.src = imageUrl || place;
    img.alt = getProductName(product);
    img.className = 'product-image';

    // Application Time Badge (AM/PM) — On image, top-right corner, vertically stacked
    const appTime = product.application_time || product.usage_time || product.recommended_time || product.time_of_use;
    if (appTime) {
      const timeText = String(appTime).toUpperCase();
      const sunIcon = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>';
      const moonIcon = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';

      function oneBadge(label, ic) {
        const el = document.createElement('div');
        el.className = 'product-time-badge';
        el.innerHTML = '<span class="time-icon">' + ic + '</span><span class="time-text">' + label + '</span>';
        return el;
      }

      if (timeText.includes('BOTH') || timeText.includes('AM/PM')) {
        const wrap = document.createElement('div');
        wrap.className = 'product-time-badges-wrap';
        wrap.appendChild(oneBadge('AM', sunIcon));
        wrap.appendChild(oneBadge('PM', moonIcon));
        imageContainer.appendChild(wrap);
      } else {
        let ic = '';
        let label = '';
        if (timeText.includes('AM') || timeText.includes('MORNING') || timeText.includes('DAY')) {
          ic = sunIcon; label = 'AM';
        } else if (timeText.includes('PM') || timeText.includes('NIGHT') || timeText.includes('EVENING')) {
          ic = moonIcon; label = 'PM';
        } else {
          ic = sunIcon; label = timeText.length > 6 ? timeText.substring(0, 6) : timeText;
        }
        imageContainer.appendChild(oneBadge(label, ic));
      }
    }


    var content = document.createElement('div');
    content.className = 'product-content';

    var name = document.createElement('h3');
    name.className = 'product-name';
    name.textContent = getProductName(product);


    var priceContainer = document.createElement('div');
    priceContainer.className = 'product-price-container';

    var priceAndKnowMore = document.createElement('div');
    priceAndKnowMore.className = 'price-knowmore-row';

    var price = document.createElement('span');
    price.className = 'product-price';
    var pVal = getProductPrice(product);
    price.textContent = pVal != null ? ('₹' + pVal.toFixed(0)) : '';

    var knowMoreBtn = document.createElement('button');
    knowMoreBtn.className = 'know-more-btn';
    knowMoreBtn.textContent = 'Know More';
    knowMoreBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const url = product._resolvedUrl || getProductUrl(product);
      if (url) window.open(url, '_blank');
    };

    priceAndKnowMore.appendChild(knowMoreBtn);
    priceAndKnowMore.appendChild(price);
    priceContainer.appendChild(priceAndKnowMore);

    // LIGHTNING FAST: Use pre-fetched cache to instantly resolve images
    const shopifyProductId = product.shopify_product_id ? String(product.shopify_product_id) : null;

    if (shopifyProductId && productImageCache && productImageCache[shopifyProductId]) {
      const cached = productImageCache[shopifyProductId];
      if (cached.image && cached.image !== img.src) {
        img.src = cached.image;
        img.classList.add('fade-in');
      }
      if (cached.url) product._resolvedUrl = cached.url;
    } else if (shopifyProductId) {
      prefetchProductImages([shopifyProductId]).then(cache => {
        if (cache[shopifyProductId]) {
          const cached = cache[shopifyProductId];
          if (cached.image && cached.image !== img.src) {
            img.src = cached.image;
            img.classList.add('fade-in');
          }
          if (cached.url) product._resolvedUrl = cached.url;
        }
      }).catch(() => { });
    }

    img.onerror = function () {
      this.src = place;
    };
    imageContainer.appendChild(img);

    var actionsContainer = document.createElement('div');
    actionsContainer.className = 'product-actions-container';

    var qtyContainer = document.createElement('div');
    qtyContainer.className = 'product-qty-wrapper';
    qtyContainer.style.display = 'none';

    var minusBtn = document.createElement('button');
    minusBtn.className = 'qty-btn minus';
    minusBtn.textContent = '-';

    var qtyInput = document.createElement('span');
    qtyInput.className = 'qty-display';
    qtyInput.textContent = '1';

    var plusBtn = document.createElement('button');
    plusBtn.className = 'qty-btn plus';
    plusBtn.textContent = '+';

    let currentQty = 1;

    var viewCartBtn = document.createElement('button');
    viewCartBtn.className = 'view-cart-btn';
    viewCartBtn.style.display = 'none';
    viewCartBtn.title = 'View Cart';
    viewCartBtn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
      window.location.href = root + 'cart';
    };
    viewCartBtn.innerHTML = THEME_CART_ICON_SVG;
    viewCartBtn.setAttribute('aria-label', 'View bag');
    viewCartBtn.setAttribute('title', 'View bag');

    var actionBtn = document.createElement('button');
    actionBtn.className = 'product-action-btn';
    actionBtn.setAttribute('aria-label', 'Add to bag');
    actionBtn.innerHTML = getProductAddToBagMarkup();

    const getVariantId = () => resolveVariantId(product);
    // Cart is now line-item based via cart_item_id, so AM/PM slot ownership hacks are unnecessary.

    minusBtn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      let vid = getVariantId();
      if (!vid) return;
      // Mark interaction slot before cart sync so sibling AM/PM card doesn't mirror.
      activeRoutineCardSlots.add(slotKey);
      if (currentQty > 1) {
        currentQty--;
        qtyInput.textContent = currentQty;
        updateCartItem(vid, currentQty, null, product);
      } else {
        updateCartItem(vid, 0, (success) => {
          if (success) {
            activeRoutineCardSlots.delete(slotKey);
            qtyContainer.style.display = 'none';
            viewCartBtn.style.display = 'none';
            actionBtn.style.display = 'flex';
            actionBtn.innerHTML = getProductAddToBagMarkup();
            currentQty = 1;
            qtyInput.textContent = '1';
          }
        }, product);
      }
    };

    plusBtn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      let vid = getVariantId();
      if (!vid) return;
      // Mark interaction slot before cart sync so sibling AM/PM card doesn't mirror.
      activeRoutineCardSlots.add(slotKey);
      currentQty++;
      qtyInput.textContent = currentQty;
      updateCartItem(vid, currentQty, null, product);
    };

    qtyContainer.appendChild(minusBtn);
    qtyContainer.appendChild(qtyInput);
    qtyContainer.appendChild(plusBtn);

    actionBtn.onclick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      const variantId = getVariantId();
      if (variantId) {
        // Mark slot immediately (before addToCart triggers cart sync).
        activeRoutineCardSlots.add(slotKey);
        addToCart(variantId, this, 1, product).then(() => {
          activeRoutineCardSlots.add(slotKey);
          currentQty = 1;
          actionBtn.style.display = 'none';
          qtyContainer.style.display = 'flex';
          viewCartBtn.style.display = 'flex';
        }).catch(() => { });
      } else {
        // Never navigate on Add to Bag. Only cart icon should navigate.
        if (window.ChatbotViewController && typeof window.ChatbotViewController.showToast === 'function') {
          window.ChatbotViewController.showToast('This product cannot be added right now', 'error');
        }
      }
    };

    actionsContainer.appendChild(qtyContainer);
    actionsContainer.appendChild(actionBtn);
    actionsContainer.appendChild(viewCartBtn);

    priceContainer.appendChild(actionsContainer);

    content.appendChild(name);
    content.appendChild(priceContainer);

    card.appendChild(imageContainer);
    card.appendChild(content);
    card.updateUIState = function (qty) {
      currentQty = qty > 0 ? qty : 1;
      if (qty > 0) {
        qtyInput.textContent = qty;
        qtyContainer.style.display = 'flex';
        viewCartBtn.style.display = 'flex';
        actionBtn.style.display = 'none';
      } else {
        qtyContainer.style.display = 'none';
        viewCartBtn.style.display = 'none';
        actionBtn.style.display = 'flex';
        actionBtn.innerHTML = getProductAddToBagMarkup();
        currentQty = 1;
        qtyInput.textContent = '1';
      }
    };

    // Store variant ID for syncing (time-aware, so AM/PM variants don't collide)
    const initialVid = resolveVariantId(product);
    if (initialVid) card.setAttribute('data-variant-id', String(normalizeCartVariantId(initialVid)));

    return card;
  }

  function getSampleProducts() {
    return [];
  }

  function createProductCarousel(grid) {
    const wrapper = document.createElement('div');
    wrapper.className = 'products-carousel-wrapper';

    const leftBtn = document.createElement('button');
    leftBtn.className = 'carousel-nav-btn carousel-nav-left';
    leftBtn.style.left = '5px';
    leftBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';

    const rightBtn = document.createElement('button');
    rightBtn.className = 'carousel-nav-btn carousel-nav-right';
    rightBtn.style.right = '5px';
    rightBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';

    // STACKED ANIMATION LOGIC
    const updateStack = () => {
      const parentRect = grid.getBoundingClientRect();
      const parentCenter = parentRect.left + parentRect.width / 2;
      const children = Array.from(grid.children);
      if (!children.length) return;
      const isAtStart = grid.scrollLeft < 10;
      const isAtEnd = grid.scrollLeft + grid.offsetWidth > grid.scrollWidth - 10;

      let activeIndex = -1;
      let minDistance = Infinity;

      children.forEach((child, index) => {
        const childRect = child.getBoundingClientRect();
        const childCenter = childRect.left + childRect.width / 2;
        const distanceFromCenter = Math.abs(parentCenter - childCenter);
        if (distanceFromCenter < minDistance) {
          minDistance = distanceFromCenter;
          activeIndex = index;
        }
      });

      // Hard clamp at edges so first/last card is always single active at boundaries.
      if (isAtStart) activeIndex = 0;
      if (isAtEnd) activeIndex = children.length - 1;

      children.forEach((child, index) => {
        child.classList.remove('active', 'peek');
        if (index === activeIndex) {
          child.classList.add('active');
        } else if (Math.abs(index - activeIndex) === 1) {
          child.classList.add('peek');
        }
      });
    };

    grid.addEventListener('scroll', updateStack, { passive: true });
    window.addEventListener('resize', updateStack);

    // Smooth scrolling to siblings for button clicks
    const scrollToSibling = (direction) => {
      const active = grid.querySelector('.active') || grid.children[0];
      if (!active) return;

      const target = direction === 'next' ? active.nextElementSibling : active.previousElementSibling;
      if (target) {
        const x = target.offsetLeft - (grid.offsetWidth / 2) + (target.offsetWidth / 2);
        grid.scrollTo({ left: x, behavior: 'smooth' });
      }
    };

    leftBtn.onclick = (e) => {
      e.stopPropagation();
      scrollToSibling('prev');
    };

    rightBtn.onclick = (e) => {
      e.stopPropagation();
      scrollToSibling('next');
    };

    // Initial state
    setTimeout(updateStack, 100);

    wrapper.appendChild(leftBtn);
    wrapper.appendChild(grid);
    wrapper.appendChild(rightBtn);
    return wrapper;
  }

  let handleProductClickHandler = function (product, card) {
    if (product._resolvedUrl) { window.open(product._resolvedUrl, '_blank'); return; }
    const url = getProductUrl(product);
    if (url) { window.open(url, '_blank'); return; }
  };

  async function fetchRecommendedProducts(questionnaireAnswers, apiEndpoint, userShopifyId) {
    if (!window.ChatbotAPI) return [];
    let uid = userShopifyId;
    if (!uid || String(uid).trim() === '') {
      try {
        let g = localStorage.getItem('chatbot_guest_id');
        uid = (g && String(g).replace(/^guest_/, '')) || (Date.now().toString() + Math.floor(Math.random() * 1000).toString());
      } catch (e) { uid = Date.now().toString(); }
    }
    try {
      const data = await window.ChatbotAPI.post(apiEndpoint, {
        user_shopify_id: String(uid),
        questionnaire_response: Array.isArray(questionnaireAnswers) ? questionnaireAnswers : []
      }, { timeout: 20000, retries: 1 });
      return data;
    } catch (error) { return null; }
  }

  function prefetchProductImages(productIds) {
    if (!productIds || productIds.length === 0) return Promise.resolve({});
    if (productImageCachePromise) return productImageCachePromise;
    if (productImageCache) return Promise.resolve(productImageCache);
    productImageCachePromise = fetch('/products.json?limit=250')
      .then(r => r.json())
      .then(data => {
        const cache = {};
        if (data && data.products) {
          data.products.forEach(p => {
            const shopifyId = String(p.id);
            const variantIdByTime = { am: null, pm: null, default: null };
            try {
              if (Array.isArray(p.variants) && p.variants.length) {
                variantIdByTime.default = p.variants[0].id;
                p.variants.forEach(function (v) {
                  // Some stores encode AM/PM in option fields (option1/option2/option3), not title.
                  const t = [
                    (v && (v.title || v.name)) || '',
                    (v && v.option1) || '',
                    (v && v.option2) || '',
                    (v && v.option3) || '',
                    (v && v.sku) || ''
                  ].join(' ').toLowerCase();
                  if (!t) return;
                  if (!variantIdByTime.am && (t.includes('am') || t.includes('morning') || t.includes('day'))) {
                    variantIdByTime.am = v.id;
                  }
                  if (!variantIdByTime.pm && (t.includes('pm') || t.includes('night') || t.includes('evening'))) {
                    variantIdByTime.pm = v.id;
                  }
                });
              }
            } catch (e) { /* ignore */ }
            cache[shopifyId] = {
              image: p.featured_image || (p.images && p.images[0] && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src)),
              url: `/products/${p.handle}`,
              price: p.variants && p.variants[0] ? parseFloat(p.variants[0].price) : null,
              handle: p.handle,
              variantId: variantIdByTime.default || (p.variants && p.variants[0] ? p.variants[0].id : null),
              variantIdByTime: variantIdByTime
            };
          });
        }
        productImageCache = cache;
        // Optimization: Once cache is warm, trigger a cart sync to resolve any missing variant ID matches
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return cache;
      })
      .catch(() => {
        productImageCachePromise = null;
        return {};
      });
    return productImageCachePromise;
  }

  function applyCartIdentityScopeToRecommendations(recommendations, dataToUse) {
    var scope = '';
    try {
      var core = (dataToUse && dataToUse.data) ? dataToUse.data : dataToUse;
      scope = String(
        (core && (core.assessment_session_id || core.recommendation_id)) ||
        (dataToUse && (dataToUse.assessment_session_id || dataToUse.recommendation_id)) ||
        ''
      ).trim();
    } catch (e) { scope = ''; }
    if (!scope) return;

    function tagProduct(p) {
      if (!p || typeof p !== 'object') return;
      p._cartIdentityScope = scope;
    }

    if (Array.isArray(recommendations)) {
      recommendations.forEach(tagProduct);
      return;
    }
    if (recommendations && typeof recommendations === 'object') {
      Object.keys(recommendations).forEach(function (k) {
        if (Array.isArray(recommendations[k])) {
          recommendations[k].forEach(tagProduct);
        }
      });
    }
  }

  function showProductsPage(data = null, questionnaireAnswers = []) {
    if (data) lastData = data;
    if (questionnaireAnswers && questionnaireAnswers.length > 0) lastQuestionnaireData = questionnaireAnswers;

    const dataToUse = data || lastData;
    const answersToUse = questionnaireAnswers.length > 0 ? questionnaireAnswers : (lastQuestionnaireData || []);

    console.log("[Chatbot] showProductsPage called. Data:", dataToUse);
    if (!productsContainer) {
      console.error("[Chatbot] productsContainer is missing!");
      return;
    }
    productsContainer.style.display = 'block';
    productsContainer.style.setProperty('display', 'block', 'important');
    clearProductsRailExceptMedia();

    // Ensure parent is visible
    if (productsContainer.parentElement) {
      productsContainer.parentElement.style.display = 'flex';
    }

    let recommendations = null;

    // ROBUST DATA EXTRACTION
    if (!dataToUse) {
      recommendations = null;
    } else {
      // Unwrap 'data' level if exists (common in axios or API wrappers)
      let core = dataToUse.data || dataToUse;
      console.log("[Chatbot] Core data for extraction:", core);

      if (core.recommendations) {
        recommendations = core.recommendations;
      } else if (core.products) {
        recommendations = core.products;
      } else if (core.skincare_recommendations) {
        recommendations = core.skincare_recommendations;
      } else if (Array.isArray(core)) {
        recommendations = core;
      } else if (typeof core === 'object') {
        recommendations = core;
      }
    }
    console.log("[Chatbot] Extracted recommendations:", recommendations);
    // Important for retake/new recommendation runs:
    // ensure each routine has a scoped cart identity so old cart lines don't show as auto-added.
    applyCartIdentityScopeToRecommendations(recommendations, dataToUse);

    const allShopifyIds = [];
    const extractIdsFromCategorized = (obj) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
      Object.keys(obj).forEach(key => {
        if (Array.isArray(obj[key])) {
          obj[key].forEach(p => { if (p && p.shopify_product_id) allShopifyIds.push(String(p.shopify_product_id)); });
        }
      });
    };
    extractIdsFromCategorized(recommendations);
    if (dataToUse && Array.isArray(dataToUse.products)) {
      dataToUse.products.forEach(p => { if (p && p.shopify_product_id) allShopifyIds.push(String(p.shopify_product_id)); });
    }
    if (allShopifyIds.length > 0) prefetchProductImages(allShopifyIds);

    const page = document.createElement('div');
    page.className = 'products-page';
    const addedProductIds = new Set();
    const uncategorizedProducts = [];
    let hasProducts = false;

    const topHeader = document.getElementById('chatbot-header');
    const topHeaderTitle = document.querySelector('#chatbot-header-title span');
    const topHeaderActions = document.getElementById('chatbot-header-actions');
    const topCloseBtn = document.getElementById('chatbot-close');
    const topViewResultsBtn = document.getElementById('chatbot-view-results-header');
    const useTopHeader = !!(topHeader && topHeaderActions);

    clearInjectedTopHeaderActions();
    // Hide title and header actions until products actually load
    if (useTopHeader && topHeaderTitle) {
      topHeaderTitle.textContent = 'Loading…';
      topHeaderTitle.style.visibility = 'hidden';
    }
    if (topViewResultsBtn) topViewResultsBtn.style.display = 'none';

    // 1. DATA PREP
    let allVisibleProducts = [];
    const skinConcerns = (dataToUse && dataToUse.skin_concerns) || (dataToUse && dataToUse.data && dataToUse.data.skin_concerns);
    let displayProfile = [];
    if (Array.isArray(skinConcerns) && skinConcerns.length > 0) {
      displayProfile = skinConcerns;
    }
    var faceAnalysis = (window.ChatbotDataManager && typeof window.ChatbotDataManager.getCachedFaceAnalysis === 'function')
      ? window.ChatbotDataManager.getCachedFaceAnalysis()
      : null;

    // Header actions — hidden initially, shown after products load
    const headerActions = document.createElement('div');
    headerActions.className = 'products-header-actions';
    headerActions.id = 'chatbot-products-top-actions';
    headerActions.style.opacity = '0';
    headerActions.style.pointerEvents = 'none';
    headerActions.style.transition = 'opacity 0.3s ease';

    // Add All Button (theme bag icon + routine product count)
    const addAllBtn = document.createElement('button');
    addAllBtn.type = 'button';
    addAllBtn.className = 'add-all-bag-btn';
    addAllBtn.setAttribute('data-all-added', 'false');
    addAllBtn.setAttribute('data-bag-count-visible', 'false');
    addAllBtn.setAttribute('title', 'Add all routine products to bag');
    addAllBtn.innerHTML = renderAddAllBagButtonInner(0, false);

    // SVG Constants for icon toggling
    const skinIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
    const menuIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>';
    const closeIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';

    // Skin Profile Toggle Button
    const skinProfileBtn = document.createElement('button');
    skinProfileBtn.className = 'header-icon-btn skin-profile-btn';
    skinProfileBtn.title = 'View Skin Profile';
    skinProfileBtn.innerHTML = skinIcon;

    // Menu Toggle Button
    const menuBtn = document.createElement('button');
    menuBtn.className = 'header-icon-btn menu-btn';
    menuBtn.title = 'Options';
    menuBtn.innerHTML = menuIcon;

    // Populate Actions
    headerActions.appendChild(addAllBtn);

    // Human Icon (Skin Profile) – show when we have skin concerns and/or face scan analysis
    var hasFaceAnalysis = !!(faceAnalysis && typeof faceAnalysis === 'object' && Object.keys(faceAnalysis).length > 0);
    if (displayProfile.length > 0 || hasFaceAnalysis) {
      headerActions.appendChild(skinProfileBtn);
    }

    // Menu/Cog Icon (Retake Options) - At the far right
    headerActions.appendChild(menuBtn);

    if (useTopHeader) {
      if (topCloseBtn && topCloseBtn.parentNode === topHeaderActions) {
        topHeaderActions.insertBefore(headerActions, topCloseBtn);
      } else {
        topHeaderActions.appendChild(headerActions);
      }
    }

    // 3. DROPDOWNS

    // 3. DROPDOWNS configuration

    // --- MENU 1: SETTINGS / RETAKE (Triggered by Menu/Cog Icon) ---
    const menuDropdown = document.createElement('div');
    menuDropdown.className = 'products-menu-dropdown';
    menuDropdown.id = 'chatbot-products-menu-dropdown';

    // Option: Retake Test (Refresh Icon)
    const retakeBtn = document.createElement('button');
    retakeBtn.className = 'menu-item-btn';
    retakeBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg> Retake Full Test';
    retakeBtn.onclick = () => {
      menuDropdown.style.display = 'none';
      if (menuBtn) menuBtn.style.background = '#ffffff';
      if (onRetake) onRetake();
    };

    // Option: Take Questionnaire (List Icon)
    const retakeQuestionnaireBtn = document.createElement('button');
    retakeQuestionnaireBtn.className = 'menu-item-btn';
    retakeQuestionnaireBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"></path><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg> Retake Questions';
    retakeQuestionnaireBtn.onclick = () => {
      menuDropdown.style.display = 'none';
      if (menuBtn) menuBtn.style.background = '#ffffff';
      if (onRetakeQuestionnaire) onRetakeQuestionnaire();
    };

    // Close Button for Retake Menu
    const closeMenuBtn = document.createElement('button');
    closeMenuBtn.className = 'dropdown-close-btn';
    closeMenuBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeMenuBtn.onclick = (e) => {
      e.stopPropagation();
      menuDropdown.style.display = 'none';
      if (menuBtn) menuBtn.style.background = '#ffffff';
    };

    menuDropdown.appendChild(retakeBtn);
    menuDropdown.appendChild(retakeQuestionnaireBtn);
    menuDropdown.appendChild(closeMenuBtn);
    (topHeader || productsContainer).appendChild(menuDropdown);

    // --- UI LOGIC FUNCTIONS ---
    let showSkinReport, showFaceScanComingSoon; // Declare for closure access

    // --- MENU 2: HUMAN PROFILE (Triggered by Human Icon) ---
    const skinConcernsDropdown = document.createElement('div');
    skinConcernsDropdown.className = 'products-skin-concerns-dropdown';
    skinConcernsDropdown.id = 'chatbot-products-skin-dropdown';
    skinConcernsDropdown.style.display = 'none'; // Ensure hidden by default
    skinConcernsDropdown.style.flexDirection = 'column';

    // CLOSE BUTTON (Absolute Positioned via CSS)
    const closeSkinBtn = document.createElement('button');
    closeSkinBtn.className = 'dropdown-close-btn';
    closeSkinBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';

    closeSkinBtn.onclick = (e) => {
      e.stopPropagation();
      skinConcernsDropdown.classList.remove('skin-dropdown-visible');
      skinConcernsDropdown.style.display = 'none';
      if (topHeader && skinConcernsDropdown.parentNode !== topHeader) topHeader.appendChild(skinConcernsDropdown);
      if (productsContainer) productsContainer.classList.remove('skin-dropdown-open');
      var contentContainer = document.getElementById('chatbot-content-container');
      if (contentContainer) {
        contentContainer.classList.remove('skin-dropdown-open');
      }
      var box = document.getElementById('chatbot-box');
      if (box) box.classList.remove('skin-dropdown-open');
      if (skinProfileBtn) skinProfileBtn.style.background = '#ffffff';
    };

    skinConcernsDropdown.appendChild(closeSkinBtn);

    // Action Buttons Container
    const reportActions = document.createElement('div');
    reportActions.className = 'skin-dropdown-actions';
    reportActions.style.display = 'flex';
    reportActions.style.flexDirection = 'row';
    reportActions.style.justifyContent = 'flex-start';
    reportActions.style.gap = '8px';
    reportActions.style.marginBottom = '12px';
    reportActions.style.width = '100%';

    function stopChatbotVideoPlayback() {
      try {
        if (window.ChatbotViewController && typeof window.ChatbotViewController.stopAllChatbotMedia === 'function') {
          window.ChatbotViewController.stopAllChatbotMedia();
        }
      } catch (e) { /* ignore */ }
    }

    const viewAnalysisBtn = document.createElement('button');
    viewAnalysisBtn.className = 'menu-item-btn'; // Use consistent class
    viewAnalysisBtn.style.background = '#ff6b35';
    viewAnalysisBtn.style.color = '#ffffff';
    viewAnalysisBtn.style.border = 'none';
    viewAnalysisBtn.style.fontSize = '12.5px'; // Increase as requested
    viewAnalysisBtn.textContent = 'Skin Analysis';
    viewAnalysisBtn.onclick = (e) => {
      e.stopPropagation();
      stopChatbotVideoPlayback();
      if (showSkinReport) showSkinReport();
    };

    const facescanBtn = document.createElement('button');
    facescanBtn.className = 'menu-item-btn'; // Use consistent class
    facescanBtn.style.fontSize = '12.5px'; // Increase as requested
    facescanBtn.textContent = 'Face Scan';
    facescanBtn.onclick = (e) => {
      e.stopPropagation();
      stopChatbotVideoPlayback();
      if (showFaceScanComingSoon) showFaceScanComingSoon();
    };

    reportActions.appendChild(viewAnalysisBtn);
    reportActions.appendChild(facescanBtn);
    skinConcernsDropdown.appendChild(reportActions);

    // --- CONTENT CONTAINER (scrollable so full height + scroll works) ---
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'products-skin-dropdown-content';
    contentWrapper.style.width = '100%';

    // 1. SIMPLE REPORT CONTENT (Just the list)
    const reportContainer = document.createElement('div');
    reportContainer.className = 'skin-analysis-report';

    // Subtle Text Header as requested
    const reportSubHeader = document.createElement('div');
    reportSubHeader.textContent = 'Skin Analysis Report';
    reportSubHeader.style.fontSize = '14px';
    reportSubHeader.style.fontWeight = '800';
    reportSubHeader.style.color = '#1e293b';
    reportSubHeader.style.marginBottom = '12px';
    reportSubHeader.style.textTransform = 'uppercase';
    reportSubHeader.style.letterSpacing = '0.6px';
    reportContainer.appendChild(reportSubHeader);

    // No "Medical Header" / "Date" stuff - removed as requested ("keep normal text")

    // Helper to categorize skin concerns into logical groups for the report UI
    function categorizeConcerns(concerns) {
      const categories = {
        'SKIN TYPE': [],
        'SENSITIVITY': [],
        'SKIN CONCERNS': [],
        'AGE': [],
        'GENDER': [],
        'SPECIAL NOTES': []
      };

      const capitalize = (s) => {
        if (!s) return s;
        // Trim and take actual first character for capitalization
        const trimmed = String(s).trim();
        if (!trimmed) return trimmed;
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
      };

      concerns.forEach(c => {
        let label = String(c).trim();
        const lc = label.toLowerCase();

        // Standardize: Replace PIH with Post-acne dark marks
        if (lc === 'pih' || lc === 'pih (post-acne dark marks)' || lc.includes('post-inflammatory hyperpigmentation')) {
          label = 'Post-acne dark marks';
        }

        if (lc.includes('skin type') || lc.includes('normal skin')) {
          // Clean up "Combination Skin Type" -> "Combination"
          const type = label.replace(/\s*skin type/gi, '').replace(/\s*skin/gi, '').trim();
          categories['SKIN TYPE'].push(capitalize(type));
        } else if (lc.includes('sensitive') || lc.includes('reactive') || lc.includes('irritated')) {
          categories['SENSITIVITY'].push(capitalize(label));
        } else if (lc.includes('pregnancy') || lc.includes('lactation') || lc.includes('pregnant')) {
          categories['SPECIAL NOTES'].push('Pregnant/Lactating');
        } else if (lc.includes('male') || lc.includes('female')) {
          categories['GENDER'].push(capitalize(label));
        } else if ((lc.includes('years') || lc.includes('age')) && !lc.includes('pore') && !lc.includes('ageing') && !lc.includes('aging')) {
          // It's likely the actual Age category (e.g., "Less than 25 years" or "No problem less than 25 years")
          categories['AGE'].push(capitalize(label));
        } else if (lc.includes('less than') || lc.includes('more than')) {
          categories['AGE'].push(capitalize(label));
        } else {
          // Default to Skin Concerns
          categories['SKIN CONCERNS'].push(capitalize(label));
        }
      });

      // Cleanup: remove empty categories
      const result = {};
      Object.keys(categories).forEach(k => {
        if (categories[k].length > 0) result[k] = categories[k];
      });
      return result;
    }

    if (displayProfile.length > 0) {
      const concernsList = document.createElement('div');
      concernsList.style.display = 'flex';
      concernsList.style.flexDirection = 'column';
      concernsList.style.gap = '10px';
      concernsList.style.marginTop = '4px';

      const categorized = categorizeConcerns(displayProfile);

      Object.keys(categorized).forEach(category => {
        const values = categorized[category];
        const row = document.createElement('div');
        row.className = 'skin-analysis-row';

        const keyEl = document.createElement('span');
        keyEl.className = 'skin-analysis-label';
        keyEl.textContent = '• ' + category;

        const dashEl = document.createElement('span');
        dashEl.className = 'skin-analysis-dash';
        dashEl.textContent = '-';

        const valEl = document.createElement('span');
        valEl.className = 'skin-analysis-value';
        if (category === 'SKIN CONCERNS') {
          valEl.innerHTML = values.map(v => `<span class="skin-analysis-value-item">• ${v}</span>`).join('');
        } else {
          valEl.textContent = values.join(', ');
        }

        row.appendChild(keyEl);
        row.appendChild(dashEl);
        row.appendChild(valEl);
        concernsList.appendChild(row);
      });

      reportContainer.appendChild(concernsList);
    } else {
      const noData = document.createElement('div');
      noData.textContent = "No specific concerns identified.";
      noData.style.fontSize = '13px';
      noData.style.color = '#64748b';
      noData.style.fontStyle = 'italic';
      reportContainer.appendChild(noData);
    }
    contentWrapper.appendChild(reportContainer);

    // 2. FACE SCAN ANALYSIS (from POST /tools/get-skin-analysis-from-image) – BTBP-style response
    function humanizeTagName(name) {
      if (!name || typeof name !== 'string') return name;
      const upper = name.toUpperCase();
      if (upper === 'PIH') return 'Post-acne dark marks';
      return name.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }).trim();
    }

    function renderFaceScanAnalysis(data) {
      var wrap = document.createElement('div');
      wrap.className = 'face-scan-analysis-wrap';
      wrap.style.display = 'flex';
      wrap.style.flexDirection = 'column';
      wrap.style.gap = '10px';

      var header = document.createElement('div');
      header.textContent = 'Face Scan Analysis';
      header.style.fontSize = '14px';
      header.style.fontWeight = '800';
      header.style.color = '#1e293b';
      header.style.marginBottom = '12px';
      header.style.textTransform = 'uppercase';
      header.style.letterSpacing = '0.6px';
      wrap.appendChild(header);

      var tags = data.Tags || data.tags;
      if (Array.isArray(tags) && tags.length > 0) {
        var list = document.createElement('ul');
        list.className = 'face-scan-tags-list';
        list.style.listStyleType = 'none';
        list.style.padding = '0';
        list.style.margin = '0';
        list.style.display = 'flex';
        list.style.flexDirection = 'column';
        list.style.gap = '10px';
        tags.forEach(function (tag, index) {
          var status = tag.Status || tag.status;
          var tagValues = tag.TagValues || tag.tagValues || [];
          var displayStr = '';

          // 1. Find the best value to display
          if (Array.isArray(tagValues) && tagValues.length > 0) {
            // Find "Combined" or "Total" score if it exists (usually more accurate than individual areas)
            var bestV = tagValues.find(function (v) {
              var vn = (v.ValueName || v.valueName || '').toLowerCase();
              return vn === 'combined' || vn === 'total' || vn === 'overall';
            });

            // If no combined value, pick the first one with a non-empty Value
            if (!bestV) {
              bestV = tagValues.find(function (v) {
                return v.Value != null && String(v.Value).trim() !== '';
              });
            }

            // Fallback to the first value in the array if we still haven't found anything
            if (!bestV && tagValues.length > 0) {
              bestV = tagValues[0];
            }

            if (bestV) {
              var val = (bestV.Value != null) ? String(bestV.Value).trim() : '';
              var units = (bestV.Units || '').toString().toLowerCase();

              if (val !== '') {
                if (units === 'number') {
                  displayStr = 'Score: ' + val + ' (0–10 scale)';
                } else {
                  displayStr = val + (bestV.Units ? ' ' + bestV.Units : '');
                }
              }
            }
          }

          var label = humanizeTagName(tag.TagName || tag.tagName || '');
          var row = document.createElement('li');
          row.className = 'skin-analysis-row';

          var labelEl = document.createElement('div');
          labelEl.className = 'skin-analysis-label';
          labelEl.textContent = '• ' + label;
          row.appendChild(labelEl);

          var dashEl = document.createElement('div');
          dashEl.className = 'skin-analysis-dash';
          dashEl.textContent = '-';
          row.appendChild(dashEl);

          var valEl = document.createElement('div');
          valEl.className = 'skin-analysis-value';
          // If we have a successful status and a valid display string, show it
          if (status && displayStr) {
            valEl.textContent = displayStr;
          } else {
            // Handle fallback messages (Errors or "OK" with no values)
            valEl.classList.add('analysis-value-message');
            var msg = (tag.Message || tag.message || 'Not detected').toString();

            if (msg.toLowerCase().indexOf('not enough resolution') !== -1 || msg.toLowerCase().indexOf('could not detect') !== -1) {
              valEl.textContent = 'Could not analyse (need clearer photo)';
            } else if (msg === 'OK' && !displayStr) {
              // If status is true but we have no value, it often means the result is "Normal" or "Balanced"
              // Specifically for SKIN_CATEGORY, "Normal" is a very standard result.
              var upperTag = (tag.TagName || tag.tagName || '').toUpperCase();
              if (upperTag === 'SKIN_CATEGORY') {
                valEl.textContent = 'Normal / Balanced';
              } else {
                valEl.textContent = 'Normal / Not detected';
              }
            } else {
              valEl.textContent = msg;
            }
          }
          row.appendChild(valEl);
          list.appendChild(row);
        });
        wrap.appendChild(list);
      }

      return wrap;
    }

    const faceScanSection = document.createElement('div');
    faceScanSection.style.display = 'none';
    faceScanSection.style.flexDirection = 'column';
    faceScanSection.style.padding = '16px 0 0';
    faceScanSection.style.gap = '8px';

    if (faceAnalysis && typeof faceAnalysis === 'object' && Object.keys(faceAnalysis).length > 0) {
      var hasTags = Array.isArray(faceAnalysis.Tags || faceAnalysis.tags) && (faceAnalysis.Tags || faceAnalysis.tags).length > 0;
      if (hasTags || faceAnalysis.Message || faceAnalysis.message) {
        faceScanSection.appendChild(renderFaceScanAnalysis(faceAnalysis));
      } else {
        var list = document.createElement('ul');
        list.style.listStyleType = 'none';
        list.style.padding = '0';
        list.style.margin = '0';
        list.style.display = 'flex';
        list.style.flexDirection = 'column';
        list.style.gap = '6px';
        Object.keys(faceAnalysis).forEach(function (key) {
          if (key === 'Tags' || key === 'tags') return;
          var val = faceAnalysis[key];
          var row = document.createElement('li');
          row.style.fontSize = '12px';
          row.style.color = '#334155';
          row.style.lineHeight = '1.4';
          var disp = val == null ? '—' : (Array.isArray(val) ? val.map(function (v) { return typeof v === 'object' ? JSON.stringify(v) : String(v); }).join(', ') : (typeof val === 'object' ? JSON.stringify(val) : String(val)));
          row.innerHTML = '<span style="color:#ff6b35; font-weight:bold; margin-right:6px;">' + String(key).replace(/_/g, ' ') + ':</span> ' + disp;
          list.appendChild(row);
        });
        faceScanSection.innerHTML = '<div style="font-size:11px; font-weight:700; color:#1e293b; margin-bottom:8px; text-transform:uppercase; letter-spacing:0.5px;">Face Scan Analysis</div>';
        faceScanSection.appendChild(list);
      }
    } else {
      faceScanSection.style.alignItems = 'center';
      faceScanSection.style.justifyContent = 'center';
      faceScanSection.style.padding = '30px 20px';
      faceScanSection.style.textAlign = 'center';

      const scanIcon = document.createElement('div');
      scanIcon.style.fontSize = '32px';
      scanIcon.style.marginBottom = '12px';
      scanIcon.textContent = '📸';

      const scanTitle = document.createElement('div');
      scanTitle.style.fontSize = '14px';
      scanTitle.style.fontWeight = '700';
      scanTitle.style.color = '#1e293b';
      scanTitle.style.marginBottom = '8px';
      scanTitle.textContent = 'Face Scan Analysis';

      const scanDesc = document.createElement('div');
      scanDesc.style.fontSize = '12px';
      scanDesc.style.color = '#64748b';
      scanDesc.style.lineHeight = '1.4';
      scanDesc.style.marginBottom = '16px';
      scanDesc.textContent = 'To get your detailed face scan report, please upload or take a photo.';

      const scanBtn = document.createElement('button');
      scanBtn.className = 'menu-item-btn';
      scanBtn.style.background = '#ff6b35';
      scanBtn.style.color = '#ffffff';
      scanBtn.style.border = 'none';
      scanBtn.style.margin = '0 auto';
      scanBtn.textContent = 'Take Face Scan';
      scanBtn.onclick = (e) => {
        e.stopPropagation();
        // Close the dropdown before redirecting
        if (skinConcernsDropdown) skinConcernsDropdown.style.display = 'none';
        if (productsContainer) productsContainer.classList.remove('skin-dropdown-open');
        if (skinProfileBtn) skinProfileBtn.style.background = '#ffffff';

        if (onFaceScan) onFaceScan();
      };

      faceScanSection.innerHTML = '';
      faceScanSection.appendChild(scanIcon);
      faceScanSection.appendChild(scanTitle);
      faceScanSection.appendChild(scanDesc);
      faceScanSection.appendChild(scanBtn);
    }
    contentWrapper.appendChild(faceScanSection);

    skinConcernsDropdown.appendChild(contentWrapper);
    (topHeader || productsContainer).appendChild(skinConcernsDropdown);

    // DEFINE LOGIC FUNCTIONS
    showSkinReport = () => {
      stopChatbotVideoPlayback();
      viewAnalysisBtn.style.background = '#ff6b35';
      viewAnalysisBtn.style.color = '#ffffff';
      viewAnalysisBtn.style.border = 'none';

      facescanBtn.style.background = '#ffffff';
      facescanBtn.style.color = '#334155';
      facescanBtn.style.border = '1px solid #e2e8f0';

      reportContainer.style.display = 'block';
      faceScanSection.style.display = 'none';
    };

    showFaceScanComingSoon = () => {
      stopChatbotVideoPlayback();
      facescanBtn.style.background = '#ff6b35';
      facescanBtn.style.color = '#ffffff';
      facescanBtn.style.border = 'none';

      viewAnalysisBtn.style.background = '#ffffff';
      viewAnalysisBtn.style.color = '#334155';
      viewAnalysisBtn.style.border = '1px solid #e2e8f0';

      reportContainer.style.display = 'none';
      faceScanSection.style.display = 'flex';
    };

    // Initialize
    showSkinReport(); // Default view

    // 4. TOGGLE LOGIC - SEPARATE HANDLERS (using constants defined above)

    // HUMAN ICON -> Skin Profile & Analysis
    skinProfileBtn.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      stopChatbotVideoPlayback();

      const isHidden = skinConcernsDropdown.style.display !== 'flex';

      // Close other
      menuDropdown.style.display = 'none';
      menuBtn.style.background = '#ffffff';

      // Toggle – add class on open so dropdown isn't clipped (full height + scroll)
      function setSkinDropdownOpen(open) {
        if (productsContainer) {
          if (open) productsContainer.classList.add('skin-dropdown-open');
          else productsContainer.classList.remove('skin-dropdown-open');
        }
        var contentContainer = document.getElementById('chatbot-content-container');
        if (contentContainer) {
          if (open) contentContainer.classList.add('skin-dropdown-open');
          else contentContainer.classList.remove('skin-dropdown-open');
        }
        var box = document.getElementById('chatbot-box');
        if (box) {
          if (open) box.classList.add('skin-dropdown-open');
          else box.classList.remove('skin-dropdown-open');
        }
      }
      setSkinDropdownOpen(isHidden);
      if (isHidden) {
        skinConcernsDropdown.classList.add('skin-dropdown-visible');
        skinConcernsDropdown.style.display = 'flex';
        var containerForFullHeight = document.getElementById('chatbot-content-container') || productsContainer;
        if (containerForFullHeight) containerForFullHeight.appendChild(skinConcernsDropdown);
      } else {
        skinConcernsDropdown.classList.remove('skin-dropdown-visible');
        skinConcernsDropdown.style.display = 'none';
        if (topHeader && skinConcernsDropdown.parentNode !== topHeader) topHeader.appendChild(skinConcernsDropdown);
      }
      skinProfileBtn.style.background = isHidden ? '#f1f5f9' : '#ffffff';
    };

    // SETTINGS/RETAKE ICON -> Retake Options
    menuBtn.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();

      const isHidden = menuDropdown.style.display !== 'flex';

      // Close other
      skinConcernsDropdown.style.display = 'none';
      skinProfileBtn.style.background = '#ffffff';

      // Toggle
      menuDropdown.style.display = isHidden ? 'flex' : 'none';
      menuBtn.style.background = isHidden ? '#f1f5f9' : '#ffffff';
    };

    // Close logic 
    document.addEventListener('click', (e) => {
      const headerHost = topHeader || productsContainer;
      if (!headerHost.contains(e.target) && !skinConcernsDropdown.contains(e.target)) {
        menuDropdown.style.display = 'none';
        skinConcernsDropdown.classList.remove('skin-dropdown-visible');
        skinConcernsDropdown.style.display = 'none';
        if (topHeader && skinConcernsDropdown.parentNode !== topHeader) topHeader.appendChild(skinConcernsDropdown);
        if (productsContainer) productsContainer.classList.remove('skin-dropdown-open');
        var contentContainer = document.getElementById('chatbot-content-container');
        if (contentContainer) {
          contentContainer.classList.remove('skin-dropdown-open');
        }
        var box = document.getElementById('chatbot-box');
        if (box) box.classList.remove('skin-dropdown-open');
        menuBtn.style.background = '#ffffff';
        skinProfileBtn.style.background = '#ffffff';
      }
    });

    // Functions removed as elements no longer exist.
    // Logic is now simplified to just toggle the dropdown.

    // Initialize Default View
    showSkinReport();

    addAllBtn.onclick = async (e) => {
      // When the button shows the bag icon + count, treat it as "view cart" — same as the storefront cart badge.
      // (Previously we only redirected when data-all-added was true, so a partial routine in the cart still looked
      // like a cart chip but clicking ran "add all" again and duplicated lines.)
      const goToCart =
        addAllBtn.getAttribute('data-bag-count-visible') === 'true' ||
        addAllBtn.getAttribute('data-all-added') === 'true';
      if (goToCart) {
        e.preventDefault();
        e.stopPropagation();
        var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
        window.location.href = root + 'cart';
        return;
      }
      if (!allVisibleProducts.length) return;

      addAllBtn.disabled = true;
      addAllBtn.innerHTML = 'Adding…' + chatbotVisuallyHidden('Adding to bag');

      // 1. Check for missing variants and pre-fetch if needed
      const missingVariantProducts = allVisibleProducts.filter(p => !resolveVariantId(p));
      if (missingVariantProducts.length > 0) {
        const idsToFetch = missingVariantProducts
          .map(p => String(p.shopify_product_id || p.product_id || p.id || ''))
          .filter(id => id && !id.includes('gid'));

        if (idsToFetch.length > 0) {
          console.log("[Chatbot] Fetching missing variant data for:", idsToFetch);
          try {
            await prefetchProductImages(idsToFetch);
          } catch (e) { console.error("Prefetch failed", e); }
        }
      }

      // 2. Prepare Items (numeric variant ids for Cart API)
      // Dedupe by variant id only: AM + PM slots are different variants and must become separate lines.
      // (Product-id dedupe incorrectly collapsed AM/PM into one line when IDs matched.)
      const itemsRaw = allVisibleProducts.map(p => {
        const vid = resolveVariantId(p);
        const id = normalizeCartVariantId(vid);
        if (!id) console.warn("Missing variant ID for add-all:", p);
        return { id: id, quantity: 1, cart_item_id: getCartItemId(p) };
      }).filter(i => i.id);
      const seenItemIds = new Set();
      const items = itemsRaw.filter(function (it) {
        const key = String(it.cart_item_id || '') || String(it.id);
        if (seenItemIds.has(key)) return false;
        seenItemIds.add(key);
        return true;
      });

      if (items.length === 0) {
        addAllBtn.innerHTML = 'Unavailable' + chatbotVisuallyHidden('Products unavailable');
        setTimeout(() => {
          var _a = addAllBtn.getAttribute('data-all-added') === 'true';
          addAllBtn.innerHTML = renderAddAllBagButtonInner(0, _a);
          addAllBtn.disabled = false;
        }, 2000);
        return;
      }

      addAllBtn.innerHTML = 'Adding…' + chatbotVisuallyHidden('Adding to bag');

      try {
        // ADD ALL (bulk + sequential fallback)
        const addResult = await postCartAddLineItems(items);

        if (addResult.ok) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

          addAllBtn.setAttribute('data-all-added', 'true');
          addAllBtn.setAttribute('data-bag-count-visible', 'true');
          addAllBtn.setAttribute('data-assumed-added', 'true'); // Explicitly block text label
          addAllBtn.setAttribute('data-sync-lock', 'true'); // Lock UI against late cart updates
          addAllBtn.setAttribute('title', 'View bag');
          addAllBtn.innerHTML = renderAddAllBagButtonInner(items.length, true);
          addAllBtn.disabled = false; // Keep it enabled for redirect to cart

          // Remove lock after 3 seconds to allow normal syncing to resume
          setTimeout(() => addAllBtn.removeAttribute('data-sync-lock'), 3000);
          
          // Delayed sync to ensure Shopify's backend has caught up
          setTimeout(syncStorefrontCartCount, 1000);
          // Do not set every card to qty 1 here — AM/PM can share a variant id; use cart sync only.
        } else {
          console.error("Add all error:", addResult.message);
          if (window.ChatbotViewController && typeof window.ChatbotViewController.showToast === 'function') {
            window.ChatbotViewController.showToast(addResult.message || 'Could not add to bag', 'error');
          }
          addAllBtn.innerHTML = 'Failed' + chatbotVisuallyHidden(addResult.message || 'Could not add to bag');
          setTimeout(function () {
            addAllBtn.innerHTML = renderAddAllBagButtonInner(0, addAllBtn.getAttribute('data-all-added') === 'true');
          }, 2500);
        }
      } catch (e) {
        console.error("Add all network error:", e);
        addAllBtn.innerHTML = 'Error' + chatbotVisuallyHidden('Network error');
        setTimeout(function () {
          addAllBtn.innerHTML = renderAddAllBagButtonInner(0, addAllBtn.getAttribute('data-all-added') === 'true');
        }, 2000);
      }

      addAllBtn.disabled = false;
      // Re-sync once loading state ends (refresh is skipped while button is disabled).
      syncStorefrontCartCount();
    };

    // Attach Header to Page
    // Keep only top header controls; remove duplicate in-products header row.

    if (true) { // Same carousel layout all viewports; serum grouping logic in buildSerumParentGroups.
      const seenKeysOnMobile = new Set();
      const mobileMainContainer = document.createElement('div');
      mobileMainContainer.className = 'mobile-products-unified-container';

      // Stable dedupe key: prevents same product duplicating across payload shapes.
      function getProductSeenKey(p, index) {
        const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
        const suffix = p.variant_id || p.shopify_variant_id || p.application_time || p.usage_time || p.recommended_time || 'default';
        return (pid && String(pid)) ? pid + '_' + String(suffix) : '';
      }

      // 1. Process Categorized Products from API
      if (recommendations && typeof recommendations === 'object' && !Array.isArray(recommendations)) {
        // Enforce specific category sequence: Pre-cleanser, Face wash, Serum, Spot, Moisturizer, Sunscreen
        const getCategoryRank = (key) => {
          const k = key.toLowerCase();
          if (k.includes('pre')) return 1;
          if (k.includes('face') || k.includes('wash')) return 2;
          if (k.includes('serum')) return 3;
          if (k.includes('spot')) return 4;
          if (k.includes('moistur')) return 5;
          if (k.includes('sun')) return 6;
          return 99;
        };

        const sortedKeys = Object.keys(recommendations).sort((a, b) => getCategoryRank(a) - getCategoryRank(b));

        sortedKeys.forEach(key => {
          if (['skin_concerns', 'data', 'recommendations', 'status', 'msg', 'detail', 'value', 'value_name', 'units', 'score'].includes(key)) return;
          const products = recommendations[key];

          if (Array.isArray(products) && products.length > 0) {
            // Allow multiple products per category (e.g. AM + PM serum) using composite key
            let uniqueProducts = products.map((p, i) => ({ p, i, keySeen: getProductSeenKey(p, i) })).filter(({ keySeen }) => {
              return keySeen && !seenKeysOnMobile.has(keySeen);
            });

            var groupsToRender = [];
            if (categoryKeyIsSerum(key)) {
              groupsToRender = buildSerumParentGroups(uniqueProducts);
            } else {
              uniqueProducts = sortAndLimitRoutineProducts(uniqueProducts);
              if (uniqueProducts.length >= 1) {
                var splitCards = shouldSplitIntoIndividualCards(uniqueProducts);
                groupsToRender = splitCards
                  ? uniqueProducts.map(function (x) { return { timeBucket: getRoutineTimeBucket(x.p), entries: [x] }; })
                  : [{ timeBucket: getGroupTimeBucket(uniqueProducts), entries: uniqueProducts }];
              }
            }

            if (groupsToRender.length >= 1) {
              groupsToRender.forEach(function (groupSpec) {
              var groupEntries = groupSpec.entries;
              if (!groupEntries || !groupEntries.length) return;
              // Keep products from same category in the same parent card.
              const categoryCard = document.createElement('div');
              categoryCard.className = 'category-parent-card';
              categoryCard.style.scrollSnapAlign = 'center';

              const categoryTitle = document.createElement('h3');
              categoryTitle.className = 'category-parent-card-title';
              categoryTitle.textContent = formatRoutineCategoryTitle(key, groupSpec.timeBucket);
              categoryCard.appendChild(categoryTitle);

              const innerGrid = document.createElement('div');
              innerGrid.className = 'category-inner-grid';

              groupEntries.forEach(({ p, keySeen }, idx) => {
                const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
                const card = createProductCard(p);
                card.classList.add('nested');
                innerGrid.appendChild(card);
                if (idx === 0 && shouldShowEitherOneSeparator(groupEntries)) {
                  const orEl = document.createElement('div');
                  orEl.className = 'product-or-separator';
                  orEl.textContent = 'OR';
                  innerGrid.appendChild(orEl);
                }
                allVisibleProducts.push(p);
                addedProductIds.add(String(pid));
                seenKeysOnMobile.add(keySeen);
                hasProducts = true;
              });

              categoryCard.appendChild(innerGrid);

              categoryCard.addEventListener('click', function (e) {
                if (e.target.closest('.product-card')) return;
                if (!this.classList.contains('active')) {
                  const x = this.offsetLeft - (mobileMainContainer.offsetWidth / 2) + (this.offsetWidth / 2);
                  mobileMainContainer.scrollTo({ left: x, behavior: 'smooth' });
                }
              });

              mobileMainContainer.appendChild(categoryCard);
              });
            }
          }
        });
      }

      // 2. Handle Flat List / Remaining Products (Single Row, No Grouping)
      let flatSource = [];
      if (Array.isArray(recommendations)) {
        flatSource = recommendations;
      } else if (recommendations && typeof recommendations === 'object') {
        flatSource = recommendations.products || recommendations.recommendations || recommendations.skincare_recommendations || recommendations.recommended_products || (recommendations.data && Array.isArray(recommendations.data) ? recommendations.data : (recommendations.data && (recommendations.data.products || recommendations.data.recommendations))) || [];
      }

      const remainingFlat = flatSource
        .map((p, i) => ({ p, keySeen: getProductSeenKey(p, i) }))
        .filter(({ keySeen }) => keySeen && !seenKeysOnMobile.has(keySeen));

      if (remainingFlat.length > 0) {
        for (let i = 0; i < remainingFlat.length; i += 2) {
          const categoryCard = document.createElement('div');
          categoryCard.className = 'category-parent-card';
          categoryCard.style.scrollSnapAlign = 'center';

          const categoryTitle = document.createElement('h3');
          categoryTitle.className = 'category-parent-card-title';
          categoryTitle.textContent = i === 0 ? 'Recommended for You' : 'More Picks';
          categoryCard.appendChild(categoryTitle);

          const innerGrid = document.createElement('div');
          innerGrid.className = 'category-inner-grid';

          const chunk = remainingFlat.slice(i, i + 2);
          chunk.forEach(({ p, keySeen }) => {
            const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
            const card = createProductCard(p);
            card.classList.add('nested');
            innerGrid.appendChild(card);
            addedProductIds.add(String(pid));
            allVisibleProducts.push(p);
            seenKeysOnMobile.add(keySeen);
            hasProducts = true;
          });

          categoryCard.appendChild(innerGrid);

          categoryCard.addEventListener('click', function (e) {
            if (e.target.closest('.product-card')) return;
            if (!this.classList.contains('active')) {
              const x = this.offsetLeft - (mobileMainContainer.offsetWidth / 2) + (this.offsetWidth / 2);
              mobileMainContainer.scrollTo({ left: x, behavior: 'smooth' });
            }
          });

          mobileMainContainer.appendChild(categoryCard);
        }
      }

      const productsListContainer = document.createElement('div');
      productsListContainer.className = 'products-list-container';

      if (mobileMainContainer.children.length > 0) {
        const carousel = createProductCarousel(mobileMainContainer);
        productsListContainer.appendChild(carousel);
      }
      page.appendChild(productsListContainer);
    } else {
      // Desktop
      const productsListContainer = document.createElement('div');
      productsListContainer.className = 'products-list-container';

      function getProductSeenKeyDesktop(p, index) {
        const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
        const suffix = p.variant_id || p.shopify_variant_id || p.application_time || p.usage_time || p.recommended_time || 'default';
        return (pid && String(pid)) ? pid + '_' + String(suffix) : '';
      }
      const seenKeysDesktop = new Set();

      if (recommendations && typeof recommendations === 'object' && !Array.isArray(recommendations)) {
        const getCategoryRank = (key) => {
          const k = key.toLowerCase();
          if (k.includes('pre')) return 1;
          if (k.includes('face') || k.includes('wash')) return 2;
          if (k.includes('serum')) return 3;
          if (k.includes('spot')) return 4;
          if (k.includes('moistur')) return 5;
          if (k.includes('sun')) return 6;
          return 99;
        };

        const sortedKeys = Object.keys(recommendations).sort((a, b) => getCategoryRank(a) - getCategoryRank(b));

        sortedKeys.forEach(key => {
          if (['skin_concerns', 'data', 'recommendations', 'status', 'msg', 'detail', 'value', 'value_name', 'units', 'score'].includes(key)) return;
          const products = recommendations[key];

          if (Array.isArray(products) && products.length > 0) {
            let uniqueProducts = products
              .map((p, i) => ({ p, i, keySeen: getProductSeenKeyDesktop(p, i) }))
              .filter(({ keySeen }) => keySeen && !seenKeysDesktop.has(keySeen));

            var groupsToRenderDesktop = [];
            if (categoryKeyIsSerum(key)) {
              groupsToRenderDesktop = buildSerumParentGroups(uniqueProducts);
            } else {
              uniqueProducts = sortAndLimitRoutineProducts(uniqueProducts);
              if (uniqueProducts.length >= 1) {
                var splitCardsDesktop = shouldSplitIntoIndividualCards(uniqueProducts);
                groupsToRenderDesktop = splitCardsDesktop
                  ? uniqueProducts.map(function (x) { return { timeBucket: getRoutineTimeBucket(x.p), entries: [x] }; })
                  : [{ timeBucket: getGroupTimeBucket(uniqueProducts), entries: uniqueProducts }];
              }
            }

            if (groupsToRenderDesktop.length >= 1) {
              groupsToRenderDesktop.forEach(function (groupSpec) {
              var groupEntries = groupSpec.entries;
              if (!groupEntries || !groupEntries.length) return;
              const categoryCard = document.createElement('div');
              categoryCard.className = 'category-parent-card';

              const categoryTitle = document.createElement('h3');
              categoryTitle.className = 'category-parent-card-title';
              categoryTitle.textContent = formatRoutineCategoryTitle(key, groupSpec.timeBucket);
              categoryCard.appendChild(categoryTitle);

              const innerGrid = document.createElement('div');
              innerGrid.className = 'category-inner-grid';

              groupEntries.forEach(({ p, keySeen }, idx) => {
                const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
                const card = createProductCard(p);
                innerGrid.appendChild(card);
                if (idx === 0 && shouldShowEitherOneSeparator(groupEntries)) {
                  const orEl = document.createElement('div');
                  orEl.className = 'product-or-separator';
                  orEl.textContent = 'OR';
                  innerGrid.appendChild(orEl);
                }
                addedProductIds.add(String(pid));
                allVisibleProducts.push(p);
                seenKeysDesktop.add(keySeen);
                hasProducts = true;
              });

              categoryCard.appendChild(innerGrid);
              productsListContainer.appendChild(categoryCard);
              });
            }
          }
        });
      }

      let flatSource = [];
      if (Array.isArray(recommendations)) {
        flatSource = recommendations;
      } else if (recommendations && typeof recommendations === 'object') {
        flatSource = recommendations.products || recommendations.recommendations || recommendations.skincare_recommendations || recommendations.recommended_products || (recommendations.data && Array.isArray(recommendations.data) ? recommendations.data : (recommendations.data && (recommendations.data.products || recommendations.data.recommendations))) || [];
      }

      const remainingProducts = flatSource
        .map((p, i) => ({ p, keySeen: getProductSeenKeyDesktop(p, i) }))
        .filter(({ keySeen }) => keySeen && !seenKeysDesktop.has(keySeen));

      if (remainingProducts.length > 0) {
        for (let i = 0; i < remainingProducts.length; i += 2) {
          const categoryCard = document.createElement('div');
          categoryCard.className = 'category-parent-card';
          categoryCard.style.margin = '10px 0 20px 0';

          const categoryTitle = document.createElement('h3');
          categoryTitle.className = 'category-parent-card-title';
          categoryTitle.textContent = i === 0 ? 'Recommended for You' : 'More Picks';
          categoryCard.appendChild(categoryTitle);

          const innerGrid = document.createElement('div');
          innerGrid.className = 'category-inner-grid';

          const chunk = remainingProducts.slice(i, i + 2);
          chunk.forEach(({ p, keySeen }) => {
            const pid = p.shopify_product_id || p.product_id || p.id || String(p.handle || '');
            const card = createProductCard(p);
            innerGrid.appendChild(card);
            addedProductIds.add(String(pid));
            allVisibleProducts.push(p);
            seenKeysDesktop.add(keySeen);
            hasProducts = true;
          });

          categoryCard.appendChild(innerGrid);
          productsListContainer.appendChild(categoryCard);
        }
      }
      page.appendChild(productsListContainer);
    }

    // Double check if we actually added products to the DOM
    const productElements = page.querySelectorAll('.product-card');

    // remove any old error messages first
    const existingMsgs = page.querySelectorAll('.no-matches-message');
    existingMsgs.forEach(msg => msg.remove());

    if (productElements.length === 0) {
      // Automatic Fallback Fetch
      const guestId = localStorage.getItem('chatbot_guest_id');
      // Try multiple sources for ID
      let targetId = null;
      try {
        if (window.ChatbotConfig && window.ChatbotConfig.currentUserId) targetId = window.ChatbotConfig.currentUserId;
        if (!targetId && guestId) targetId = guestId.replace('guest_', '');
      } catch (e) {
        console.error("Error retrieving user ID for fallback:", e);
      }

      // Avoid infinite fallback loop
      if (targetId && !window._chatbotFallbackActive) {
        console.log("Empty results. Triggering fallback fetch for:", targetId);
        window._chatbotFallbackActive = true;

        // Show analyzing loader
        var loader = document.createElement('div');
        loader.innerHTML = '<div style="text-align:center;padding:20px;"><div class="spinner-border" style="width:24px;height:24px;border:3px solid #e2e8f0;border-top:3px solid #ff6b35;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 10px auto;"></div><div style="font-size:11px;color:#64748b;font-weight:600;">Analyzing your profile...</div></div>';
        var animStyle = document.createElement('style');
        animStyle.innerHTML = '@keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);}}';
        loader.appendChild(animStyle);
        page.appendChild(loader);

        // MANUALLY UPDATE DOM BEFORE RETURNING
        productsContainer.innerHTML = '';
        productsContainer.appendChild(page);
        ensureProductsMediaStackLast();
        productsContainer.style.display = 'block';
        if (productsContainer.parentElement) productsContainer.parentElement.style.display = 'flex';

        // Perform Direct Fetch with DELAY to allow backend processing
        const endpoint = (window.ChatbotConfig && window.ChatbotConfig.ENDPOINTS && window.ChatbotConfig.ENDPOINTS.GET_EXISTING_RECOMMENDATIONS)
          || 'https://codeskin-service-625951850094.asia-south1.run.app/tools/get-existing-recommended-products'; // FULL URL Fallback

        setTimeout(() => {
          // Use API utility
          if (window.ChatbotAPI) {
            window.ChatbotAPI.post(`${endpoint}?user_shopify_id=${targetId}`, {})
              .then(data => {
                console.log("Fallback data received", data);
                window._chatbotFallbackActive = false;
                displayProducts(data);
              })
              .catch(e => {
                console.warn("Fallback fetch failed", e);
                window._chatbotFallbackActive = false;
                productsContainer.innerHTML = '';
              });
          } else {
            // Fetch fallback
            fetch(`${endpoint}?user_shopify_id=${targetId}`, { method: 'POST' })
              .then(r => r.json())
              .then(d => {
                window._chatbotFallbackActive = false;
                displayProducts(d);
              })
              .catch(() => {
                window._chatbotFallbackActive = false;
                productsContainer.innerHTML = '';
              });
          }
        }, 1500); // 1.5s delay

        // Return to wait for async operation
        return;
      }

      // Final Empty State: Fallback ran but still no products, or no user ID found.
      if (productElements.length === 0) {
        var noRecs = document.createElement('div');
        noRecs.style.textAlign = 'center';
        noRecs.style.padding = '40px 20px';
        noRecs.innerHTML = '<p style="color:#64748b;margin-bottom:15px;">We couldn\'t generate specific matches based on this scan.</p>';

        var shopAll = document.createElement('button');
        shopAll.className = 'welcome-btn primary';
        shopAll.textContent = 'View All Products';
        shopAll.style.margin = '0 auto';
        shopAll.style.display = 'block';
        shopAll.onclick = function () { window.location.href = '/collections/all'; };

        noRecs.appendChild(shopAll);
        page.appendChild(noRecs);
      }
    } else {
      // Products found! Reset flag.
      window._chatbotFallbackActive = false;
    }

    // Always reset flag if we reached this point (either products shown or empty state handled)
    window._chatbotFallbackActive = false;

    {
      // IMPORTANT: first arg is how many *routine* items are in the cart — NOT routine size.
      // Using countProductsWithVariants() here made the badge show N before cart.js ran (looked auto-added).
      addAllBtn.innerHTML = renderAddAllBagButtonInner(0, false);
      addAllBtn.setAttribute('data-all-added', 'false');
      addAllBtn.setAttribute('data-bag-count-visible', 'false');
      addAllBtn.removeAttribute('data-assumed-added');
      addAllBtn.setAttribute('title', 'Add all routine products to bag');
    }
    clearProductsRailExceptMedia();
    productsContainer.appendChild(page);
    ensureProductsMediaStackLast();

    // Population of routine IDs and cart sync must happen after elements are in the DOM
    setCurrentRoutineVariantIdsFromProducts(allVisibleProducts);
    syncStorefrontCartCount();
    // Subsequent sync to handle cache loading/late cart results
    setTimeout(syncStorefrontCartCount, 1200);
    productsContainer.style.display = 'block';
    if (productsContainer.parentElement) productsContainer.parentElement.style.display = 'flex';
    productsContainer.scrollTop = 0;

    // Reveal header actions + title now that products are in the DOM
    if (hasProducts) {
      if (useTopHeader && topHeaderTitle) {
        topHeaderTitle.textContent = 'Your Personalized Routine';
        topHeaderTitle.style.visibility = 'visible';
      }
      setTimeout(function () {
        headerActions.style.opacity = '1';
        headerActions.style.pointerEvents = '';
      }, 120);
    } else {
      if (useTopHeader && topHeaderTitle) {
        topHeaderTitle.textContent = 'Your Routine';
        topHeaderTitle.style.visibility = 'visible';
      }
    }

    setTimeout(() => {
      productsContainer.style.display = 'block';
      productsContainer.style.visibility = 'visible';
    }, 150);
  }

  function hideProducts() {
    if (productsContainer) productsContainer.style.display = 'none';
    clearInjectedTopHeaderActions();
  }

  function refreshDisplay() {
    if (lastData) {
      showProductsPage();
    }
  }

  function clearRuntimeState() {
    // Keep productImageCache warm (performance), but clear routine/session-level state.
    lastData = null;
    lastQuestionnaireData = null;
    currentRoutineVariantIds = [];
    currentRoutineCartItemIds = [];
    currentRoutineExpectedQty = 0;
    activeRoutineCardSlots = new Set();
  }

  return {
    setDOMReferences: setDOMReferences,
    displayProducts: displayProducts,
    showProductsPage: showProductsPage,
    hideProducts: hideProducts,
    refreshDisplay: refreshDisplay,
    clearRuntimeState: clearRuntimeState,
    fetchRecommendedProducts: fetchRecommendedProducts,
    getSampleProducts: getSampleProducts,
    syncStorefrontCartCount: syncStorefrontCartCount
  };
}

window.ChatbotProducts = initChatbotProducts();
const ChatbotProducts = window.ChatbotProducts;

// Global listener for cart changes to keep Add All button synced
document.addEventListener('cart:refresh', () => {
    if (window.ChatbotProducts && window.ChatbotProducts.syncStorefrontCartCount) {
        window.ChatbotProducts.syncStorefrontCartCount();
    }
});
document.addEventListener('cart:updated', () => {
    if (window.ChatbotProducts && window.ChatbotProducts.syncStorefrontCartCount) {
        window.ChatbotProducts.syncStorefrontCartCount();
    }
});
